import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Menu } from 'lucide-react';
import { ChatArea, type ClarifyEchoOptions } from './components/ChatArea';
import { SettingsModal } from './components/SettingsModal';
import { AccountCenter } from './components/AccountCenter';
import { ErrorBoundary } from './components/ErrorBoundary';
import { FloatingUserButton } from './components/FloatingUserButton';
const AdminDashboard = React.lazy(() => import('./components/admin/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
import LandingPageV3 from './LandingPageV3';
import { LoginGatewayPageV3 } from './components/LoginGatewayPageV3';
import { SidebarSecure } from './components/SidebarSecure';
import { AppShortcuts } from './components/AppShortcuts';
import { cancelPlcGeneration, generatePlcCodeStream, recoverPlanReview } from './services/geminiService';
import { GenerationConfig, Message, ChatSession, GenerationMode, GenerationMetrics, GenerationTestHints, RepairAppendix, GenerationProgressState, PlanProcessSummary, GenerationProcessStage, EngineeringContext } from './types';
import { parseStreamedContent } from './utils/structuredResponse';
import { getLatestHistorySnapshot } from './utils/chatHistory';
import { asksToResumePlan, RESUME_PLAN_FALLBACK_PROMPT } from './utils/resumePlanIntent';
import { renameInSet, renameKey, renameMapKey, renameSessionIn } from './utils/sessionRename';
import { prepareBranchForEdit, selectActivePath, type ActiveBranchMap } from './utils/conversationPath';

/** Branch selections made before the session has an id. */
const DRAFT_BRANCH_KEY = '__draft__';
const EMPTY_BRANCHES: ActiveBranchMap = {};
import { useTranslation } from './i18n';
import { sealFinishedStageConclusions } from './utils/presentation/turnSteps';
import { consumeSessionExpiredNotice, fetchCurrentUser, hasAdminConsoleAccess, loginWithPassword, logout, type AuthUser } from './services/auth';
import { deleteRemoteSession, loadRemoteSession, loadRemoteSessions, updateRemoteSession } from './services/conversationStore';
import { persistProjectScope } from './utils/persistProjectScope';
import { applySessionLivenessToTurn, clearOrphanedStreamingFlag, keepMessagesAgainstSummary, preferLocalMessages } from './utils/sessionSummaryMerge';
import { streamingPollDelayMs } from './utils/streamingPollBackoff';
import { resolveConversationHistoryState } from './utils/conversationHistoryState';
import { resolveRestoredSession } from './utils/authNavigation';
import { hasDisplayPayloadAfterMetadata, hasDisplayPayloadAfterPipeline } from './utils/streamPayload';
import { advancePlanReviewOnApproval, mergeSessionSubmittedPlanReviewResults, mergeSubmittedPlanReviewResults, sessionHasCompletedOutput } from './utils/planReviewState';
import { preserveStreamedTurnRecord } from './utils/streamedTurnRecord';
import { withCurrentProviderCredentials } from './utils/aiModelSettings';
import { formatSessionStatusLabel } from './utils/sessionStatusLabel';
import {
  beginTurnTiaTarget,
  createServerTiaTargetWrites,
  createSessionSnapshotWriter,
  listenForSessionTiaTargetStored,
  reloadSessionTiaProjectRef,
  sessionSettingsPlcModel,
  type TurnGenerationConfig,
} from './utils/sessionTiaTarget';
import { getSavedTiaWorkMode } from './utils/plcPreferences';
import { resolveAutoSessionTitle, type TitleSource } from './utils/sessionTitle';
import {
  enrichSessionsFromIndexedDB,
  getChatStorageScope,
  getScopedLastActiveSessionId,
  loadScopedChatSessions,
  purgeStoredChatSessionsByDateRange,
  shouldHydrateRestoredSession,
  saveScopedChatSessions,
  setScopedLastActiveSessionId,
} from './utils/scopedChatStorage';
import { isLocalCopyStreaming, needsBodyFetch, type HydrationMarker } from './utils/streamingSessionHydration';
import { getItem, migrateToScoped } from './utils/scopedSettingsStorage';
import { useSettingsStore } from './stores/settingsStore';
import { useSessionTiaTargetStore } from './stores/sessionTiaTargetStore';
import { onSessionTiaTargetWritten } from './stores/tiaAgentStatusStore';
import { ToastProvider } from './components/Toast';
import * as streamState from './utils/generationStreamState';
import { getWorkflowStageGates } from './utils/workflowStageGuards';
const EngineeringWorkspace = React.lazy(() => import('./components/workspace/EngineeringWorkspace').then(m => ({ default: m.EngineeringWorkspace })));
const FilePreviewPanel = React.lazy(() => import('./components/FilePreviewPanel').then(m => ({ default: m.FilePreviewPanel })));
import { buildWorkspaceViewModel, type WorkspaceTab } from './utils/workspaceViewModel';
import { buildWorkspaceDeliverables } from './utils/workspaceDeliverables';
import { useProjectArtifacts } from './hooks/useProjectArtifacts';
import { DiagnosticsVisibilityContext } from './utils/diagnosticsVisibility';
import { hasEngineeringDashboardContent } from './utils/displayStrategy';
import {
  getWorkspacePanelState,
  resetWorkspacePanelState,
  setWorkspacePanelActiveTab,
  setWorkspacePanelExpandLevel,
  type WorkspaceExpandLevel,
  type WorkspacePanelStateBySession,
} from './utils/workspacePanelState';
import { stripAttachmentContextForDisplay } from './utils/messageDisplay';
import { CreateProjectModal } from './components/CreateProjectModal';
import { useFilePreviewStore } from './stores/filePreviewStore';
import {
  loadProjects,
  createProject,
  deleteProject,
  updateProject,
  loadProjectWorkspace,
  type ProjectRecord,
} from './services/projectStore';


type PublicAppVersionPayload = {
  appVersion?: unknown;
  version?: unknown;
  displayVersion?: unknown;
};

const DEFAULT_LOGIN_APP_VERSION = 'V3.2.5';

function normalizeAppVersionLabel(value: unknown): string {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (!raw) return '';
  return /^v/i.test(raw) ? raw : `V${raw}`;
}

async function fetchPublicAppVersion(): Promise<string> {
  try {
    const response = await fetch('/api/admin/public/app-version', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return '';
    const data = await response.json() as PublicAppVersionPayload;
    return normalizeAppVersionLabel(data.appVersion ?? data.displayVersion ?? data.version);
  } catch (error) {
    console.warn('[version] Failed to load public app version:', error);
    return '';
  }
}

type AppView = 'landing' | 'login' | 'workspace' | 'admin';
type AccountSection = 'profile' | 'security';

const SESSION_EXPIRED_MESSAGE = '登录已过期，请重新登录。';

const createInitialSessionTitle = (prompt: string) => {
  const normalizedPrompt = stripAttachmentContextForDisplay(prompt).replace(/\s+/g, ' ').trim();
  if (!normalizedPrompt) return 'New Project';
  return normalizedPrompt.length > 30 ? `${normalizedPrompt.slice(0, 30)}...` : normalizedPrompt;
};

function normalizeMessageTimestamp(message: Message, fallbackTimestamp: number): Message {
  const normalized: Message = {
    ...message,
    timestamp: typeof message.timestamp === 'number' && Number.isFinite(message.timestamp)
      ? message.timestamp
      : fallbackTimestamp,
  };

  if (normalized.role === 'assistant') {
    if (normalized.clarificationChecklist?.length) {
      normalized.clarificationChecklist = streamState.dedupeClarificationItems(normalized.clarificationChecklist);
      const gates = getWorkflowStageGates(normalized);
      if (gates.hasPendingHumanConfirmation) {
        normalized.pendingChipIds = gates.pendingRequiredHumanConfirmationIds;
      }
    }

    const parsed = parseStreamedContent(normalized.content, normalized.reasoning);
    if (parsed && normalized.parsed) {
      normalized.parsed = {
        ...parsed,
        ...normalized.parsed,
        flowchart: normalized.parsed.flowchart || parsed.flowchart,
        summary: normalized.parsed.summary || parsed.summary,
      };
    } else if (parsed) {
      normalized.parsed = parsed;
    }
  }

  return normalized;
}

function normalizeSessionForPersistence(session: ChatSession): ChatSession {
  const baseTimestamp =
    typeof session.updatedAt === 'number' && Number.isFinite(session.updatedAt)
      ? session.updatedAt
      : Date.now();

  return {
    ...session,
    messages: session.messages.map((message, index) =>
      normalizeMessageTimestamp(message, baseTimestamp + index),
    ),
  };
}

function normalizeSessionsForPersistence(sessions: ChatSession[]): ChatSession[] {
  return sessions.map((session) => normalizeSessionForPersistence(session));
}


function preserveSessionProjectScope(remoteSession: ChatSession, localSession?: ChatSession): ChatSession {
  const preservedProjectId = remoteSession.projectId || localSession?.projectId;
  if (!preservedProjectId || remoteSession.projectId === preservedProjectId) {
    return remoteSession;
  }
  return {
    ...remoteSession,
    projectId: preservedProjectId,
  };
}

function applyProjectMembershipToSessions(
  sessions: ChatSession[],
  membership: Map<string, string>,
): ChatSession[] {
  if (membership.size === 0) return sessions;
  let changed = false;
  const scoped = sessions.map((session) => {
    const projectId = session.projectId || membership.get(session.id);
    if (!projectId || session.projectId === projectId) return session;
    changed = true;
    return { ...session, projectId };
  });
  return changed ? scoped : sessions;
}

async function buildProjectSessionMembership(projects: ProjectRecord[]): Promise<Map<string, string>> {
  const membership = new Map<string, string>();
  await Promise.all(projects.map(async (project) => {
    try {
      const workspace = await loadProjectWorkspace(project.id);
      for (const session of workspace.sessions || []) {
        membership.set(session.id, session.projectId || project.id);
      }
    } catch (error) {
      console.warn('[projects] Failed to load project workspace for membership restore', project.id, error);
    }
  }));
  return membership;
}

function getMessageEngineeringContext(message: Message): EngineeringContext | undefined {
  return message.contextMemory || message.generationMetrics?.contextMemory;
}

function selectLatestEngineeringContext(
  messages: Message[],
  session?: ChatSession | null,
): EngineeringContext | undefined {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const memory = getMessageEngineeringContext(messages[index]);
    if (memory) return memory;
  }
  return session?.contextMemory;
}

function getBrowserStorage(): Storage | undefined {
  if (typeof localStorage === 'undefined') {
    return undefined;
  }

  return localStorage;
}

function loadLocalSessionsSnapshot(scope: string): ChatSession[] {
  return loadScopedChatSessions(getBrowserStorage(), scope, normalizeSessionsForPersistence);
}

function messageDisplayWeight(message: Message): number {
  const parsedWeight = [
    message.parsed?.thought,
    message.parsed?.code,
    message.parsed?.explanation,
    message.parsed?.flowchart,
    message.parsed?.summary,
    message.parsed?.verification,
  ].reduce((sum, value) => sum + (value?.length || 0), 0);
  const attachmentWeight = (message.attachments || []).reduce(
    (sum, attachment) => sum + 100 + (attachment.fileName?.length || 0),
    0,
  );

  return (
    (message.content?.length || 0)
    + (message.reasoning?.length || 0)
    + (message.planReasoning?.length || 0)
    + (message.planThinkingReasoning?.length || 0)
    + (message.planAnalysisReasoning?.length || 0)
    + (message.planGenerationReasoning?.length || 0)
    + ((message.planProcessHistory?.length || 0) * 150)
    + parsedWeight
    + attachmentWeight
    + (message.repairAppendix ? 250 : 0)
    + (message.progressState ? 50 : 0)
  );
}

function sessionDisplayWeight(session: ChatSession): number {
  return session.messages.reduce((sum, message) => sum + messageDisplayWeight(message), 0);
}

function mergeMessageAttachments(primaryMessages: Message[], fallbackMessages: Message[] | undefined): Message[] {
  if (!fallbackMessages?.length) return primaryMessages;

  const fallbackById = new Map(fallbackMessages.map((message) => [message.id, message]));
  let changed = false;
  const merged = primaryMessages.map((message) => {
    if (message.attachments?.length) return message;
    const fallbackAttachments = fallbackById.get(message.id)?.attachments;
    if (!fallbackAttachments?.length) return message;
    changed = true;
    return { ...message, attachments: fallbackAttachments };
  });

  return changed ? merged : primaryMessages;
}

function mergeRemoteAndLocalSessions(remoteSessions: ChatSession[], localSessions: ChatSession[]): ChatSession[] {
  const localById = new Map(localSessions.map((session) => [session.id, session]));
  const mergedIds = new Set<string>();

  const merged = remoteSessions.map((summarySession) => {
    mergedIds.add(summarySession.id);
    const localSession = localById.get(summarySession.id);
    // The list is a summary and carries no message bodies, so it must not be
    // read as saying a conversation is empty.
    const remoteSession = keepMessagesAgainstSummary(summarySession, localSession);
    const remoteWithLocalSubmissions = preserveSessionProjectScope(
      localSession
        ? mergeSessionSubmittedPlanReviewResults(remoteSession, localSession)
        : remoteSession,
      localSession,
    );
    const remoteWithPreservedAttachments = localSession
      ? {
          ...remoteWithLocalSubmissions,
          messages: mergeMessageAttachments(remoteWithLocalSubmissions.messages, localSession.messages),
        }
      : remoteWithLocalSubmissions;
    if (
      localSession
      && (
        localSession.isStreaming
        || (remoteSession.isStreaming && sessionDisplayWeight(localSession) > sessionDisplayWeight(remoteSession))
      )
    ) {
      const localWithRemoteSubmissions = mergeSessionSubmittedPlanReviewResults(localSession, remoteSession);
      const localMessagesWithPreservedAttachments = mergeMessageAttachments(
        localWithRemoteSubmissions.messages,
        remoteSession.messages,
      );
      return preserveSessionProjectScope({
        ...remoteWithPreservedAttachments,
        projectId: remoteWithPreservedAttachments.projectId || localWithRemoteSubmissions.projectId,
        messages: localMessagesWithPreservedAttachments,
        updatedAt: Math.max(remoteSession.updatedAt || 0, localSession.updatedAt || 0),
        isStreaming: localSession.isStreaming,
      }, localSession);
    }

    // The server records the finished turn, never the stage stream that led to
    // it, so a remote copy is always missing the account of how the turn got
    // there. Reloading must not be what erases it.
    return preserveStreamedTurnRecord(remoteWithPreservedAttachments, localSession);
  });

  for (const localSession of localSessions) {
    if (!mergedIds.has(localSession.id)) {
      merged.push(localSession);
    }
  }

  return merged.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
}

async function recoverOrphanedPlanReviews(sessions: ChatSession[]): Promise<ChatSession[]> {
  let changed = false;
  const recovered = await Promise.all(
    sessions.map(async (session) => {
      if (!session.isStreaming) return session;

      let sessionChanged = false;
      const recoveredMessages = await Promise.all(
        session.messages.map(async (message) => {
          if (message.role !== 'assistant') return message;

          const jobId = message.generationMetrics?.requestId;
          if (!jobId) return message;

          const planPhase = message.progressState?.planPhase;
          const progress = message.progress ?? 0;
          const hasPlanReview = !!message.planReview;
          const hasReviewResult = !!message.planReview?.reviewResult;

          // Only recover messages that were awaiting plan review when interrupted
          if (planPhase === 'done' && progress >= 18 && (!hasPlanReview || !hasReviewResult)) {
            try {
              const recoveredPlan = await recoverPlanReview(jobId);
              if (recoveredPlan?.planReview) {
                console.info(`[plan-recovery] Recovered plan review for jobId=${jobId}`);
                sessionChanged = true;
                return {
                  ...message,
                  planReview: recoveredPlan.planReview as any,
                  isStreaming: true,
                };
              }
              // No pending review found — plan was already processed
            } catch (err) {
              console.warn(`[plan-recovery] Failed to recover plan review for jobId=${jobId}:`, err);
            }
          }

          return message;
        }),
      );

      if (sessionChanged) {
        changed = true;
        return { ...session, messages: recoveredMessages, isStreaming: true };
      }
      return session;
    }),
  );

  if (changed) {
    console.info(`[plan-recovery] Recovered plan reviews for ${changed ? 'some' : 'no'} sessions`);
  }
  return recovered;
}

function AppSecure() {
  const { language, t } = useTranslation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const [accountInitialSection, setAccountInitialSection] = useState<AccountSection>('profile');
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loadingSessionId, setLoadingSessionId] = useState<string | null>(null);
  const [historyLoadingSessionId, setHistoryLoadingSessionId] = useState<string | null>(null);
  const [historyLoadErrorSessionId, setHistoryLoadErrorSessionId] = useState<string | null>(null);
  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [authSubmitting, setAuthSubmitting] = useState(false);
  const [view, setView] = useState<AppView>('landing');
  const [publicAppVersion, setPublicAppVersion] = useState('');
  const [workspacePanelStateBySession, setWorkspacePanelStateBySession] = useState<WorkspacePanelStateBySession>({});
  const [activeWorkspaceSourceMessageId, setActiveWorkspaceSourceMessageId] = useState<string | null>(null);
  const filePreviewOpen = useFilePreviewStore(state => state.isOpen);
  const [projects, setProjects] = useState<ProjectRecord[]>([]);
  const [sidebarView, setSidebarView] = useState<'main' | 'project'>('main');
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  // Green dot: tracks which completed sessions the user has already viewed.
  // When a session finishes generating output, its id is removed so the dot reappears.
  const [viewedSessionIds, setViewedSessionIds] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem('realplc_viewed_sessions');
      if (raw) return new Set(JSON.parse(raw) as string[]);
    } catch { /* ignore */ }
    return new Set<string>();
  });
  const [activeProjectName, setActiveProjectName] = useState('');
  const [projectSessions, setProjectSessions] = useState<ChatSession[]>([]);
  const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] = useState(false);

  // Starred sessions from all sources (independent + project-scoped)
  const starredSessions = useMemo(
    () => sessions.filter((s) => s.isStarred),
    [sessions],
  );

  const currentSessionIdRef = useRef<string | null>(null);
  const messagesRef = useRef<Message[]>([]);
  const sessionsRef = useRef<ChatSession[]>([]);
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
  const activeJobIdsRef = useRef<Map<string, string>>(new Map());
  // P2-3: Track sessions that have already had auto-title generation
  const titleGeneratedRef = useRef<Set<string>>(new Set());
  const generatingSessionRef = useRef<string | null>(null);
  const pendingDeletesRef = useRef<Set<string>>(new Set());
  // Conversations whose message bodies have already been pulled from the
  // per-session endpoint, so opening one twice costs one request.
  const hydratedSessionIdsRef = useRef<Set<string>>(new Set());
  /**
   * The `updatedAt` each watched conversation carried when this tab last pulled
   * its bodies, so the streaming tick can tell "nothing happened" from "cannot
   * tell". A ref rather than state: it is read and written inside the tick and
   * must never re-render anything.
   */
  const lastHydratedUpdatedAtRef = useRef<Map<string, HydrationMarker>>(new Map());
  /**
   * When the page last wrote what the server stored of a session's TIA target
   * into its copy (TIA 根治 P2, T2b). A server copy fetched before that write
   * must not put back what it replaced.
   */
  const [serverTiaTargetWrites] = useState(createServerTiaTargetWrites);
  const storageScope = useMemo(() => getChatStorageScope(authUser), [authUser]);

  useEffect(() => {
    currentSessionIdRef.current = currentSessionId;
  }, [currentSessionId]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    sessionsRef.current = sessions;
  }, [sessions]);

  useEffect(() => {
    let cancelled = false;
    void fetchPublicAppVersion().then((version) => {
      if (!cancelled) setPublicAppVersion(version || DEFAULT_LOGIN_APP_VERSION);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        const user = await fetchCurrentUser();
        setAuthUser(user);
        if (user) {
          setView('workspace');
        } else if (consumeSessionExpiredNotice()) {
          setAuthError(SESSION_EXPIRED_MESSAGE);
          setView('login');
        } else {
          setView('landing');
        }
      } catch (error) {
        console.error('Failed to initialize auth', error);
        if (consumeSessionExpiredNotice()) {
          setAuthError(SESSION_EXPIRED_MESSAGE);
          setView('login');
        } else {
          setAuthError(error instanceof Error ? error.message : 'Authentication failed');
          setView('landing');
        }
      } finally {
        setAuthReady(true);
      }
    })();
  }, []);

  useEffect(() => {
    if (authUser) {
      migrateToScoped();
      useSettingsStore.persist.rehydrate();
    }
  }, [authUser]);

  const handleLogout = useCallback(() => {
    void logout().finally(() => {
      useSettingsStore.getState().resetSettings();
      setIsSettingsOpen(false);
      setIsAccountOpen(false);
      setAuthUser(null);
      setView('landing');
      setCurrentSessionId(null);
      setMessages([]);
      setSessions([]);
      setWorkspacePanelStateBySession({});
      pendingDeletesRef.current.clear();
    });
  }, []);

  const handleLogoutSimple = useCallback(() => {
    void logout().finally(() => {
      useSettingsStore.getState().resetSettings();
      setIsSettingsOpen(false);
      setIsAccountOpen(false);
      setAuthUser(null);
      setView('landing');
      setWorkspacePanelStateBySession({});
      pendingDeletesRef.current.clear();
    });
  }, []);

  const handleOpenSignin = useCallback(() => {
    if (authUser) {
      setView('workspace');
      return;
    }

    setAuthError(null);
    setView('login');
  }, [authUser]);

  const handleLogin = useCallback(async (input: { identifier: string; password: string }) => {
    if (authUser) {
      setView('workspace');
      return;
    }

    setAuthSubmitting(true);
    setAuthError(null);

    try {
      const { user } = await loginWithPassword(input);
      setAuthUser(user);
      setView('workspace');
    } catch (error) {
      console.error('Failed to login', error);
      setAuthError(error instanceof Error ? error.message : 'Authentication failed');
      setView('login');
    } finally {
      setAuthSubmitting(false);
    }
  }, [authUser]);

  const handleOpenAccount = useCallback((section: AccountSection = 'profile') => {
    setAccountInitialSection(section);
    setIsSettingsOpen(false);
    setIsAccountOpen(true);
  }, []);

  const currentSession = useMemo(
    () => sessions.find((session) => session.id === currentSessionId) || null,
    [currentSessionId, sessions]
  );
  // The open conversation's TIA target, where the TIA button, the Agent
  // dialog, the post-generation card and the project picker read it (TIA 根治
  // P2, T2b), follows the conversation and its snapshot.
  const currentSessionConfig = currentSession?.sessionConfig;
  useEffect(() => {
    useSessionTiaTargetStore.getState().syncSessionTiaTarget(currentSessionId, currentSessionConfig);
  }, [currentSessionId, currentSessionConfig]);
  const workspacePanelState = useMemo(
    () => getWorkspacePanelState(workspacePanelStateBySession, currentSessionId),
    [workspacePanelStateBySession, currentSessionId],
  );
  const workspaceExpandLevel = workspacePanelState.expandLevel;
  const activeWorkspaceTab = workspacePanelState.activeTab;
  const setCurrentWorkspaceExpandLevel = useCallback((expandLevel: WorkspaceExpandLevel) => {
    setWorkspacePanelStateBySession(prev => setWorkspacePanelExpandLevel(
      prev,
      currentSessionIdRef.current,
      expandLevel,
    ));
  }, []);
  const setCurrentWorkspaceTab = useCallback((tab: WorkspaceTab) => {
    setWorkspacePanelStateBySession(prev => setWorkspacePanelActiveTab(
      prev,
      currentSessionIdRef.current,
      tab,
    ));
  }, []);
  const latestEngineeringContext = useMemo(
    () => selectLatestEngineeringContext(messages, currentSession),
    [currentSession, messages],
  );
  // A conversation started outside a project has its artifacts filed under the
  // organization's default workspace, while the session itself stays in the main
  // history where the user left it. The project id therefore only ever arrives
  // with the turn — without reading it back here the workspace looks for the
  // artifacts under no project and finds none.
  const runtimeAssignedProjectId = useMemo(() => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const registry = messages[index]?.generationMetrics?.artifactRegistry as
        { projectId?: unknown } | undefined;
      if (typeof registry?.projectId === 'string' && registry.projectId) return registry.projectId;
    }
    return undefined;
  }, [messages]);
  const workspaceProjectId = currentSession?.projectId
    || activeProjectId
    || runtimeAssignedProjectId
    || undefined;

  // Refetch the registry when a generation finishes, since that is when new
  // artifact versions appear.
  const artifactRevision = useMemo(
    () => messages.filter(m => m.role === 'assistant' && !m.isStreaming).length,
    [messages],
  );
  const { artifacts: registryArtifacts, chains: artifactVersionChains } = useProjectArtifacts(
    workspaceProjectId,
    currentSessionId || undefined,
    artifactRevision,
  );
  const workspaceDeliverables = useMemo(
    () => buildWorkspaceDeliverables(messages, language),
    [language, messages],
  );

  const workspaceViewModel = useMemo(
    () => buildWorkspaceViewModel({
      sessionId: currentSessionId || '',
      projectId: workspaceProjectId,
      messages,
      context: latestEngineeringContext,
      activeSourceMessageId: activeWorkspaceSourceMessageId ?? undefined,
      activeTab: activeWorkspaceTab,
      registryArtifacts,
      deliverableArtifacts: workspaceDeliverables,
    }),
    [currentSessionId, workspaceProjectId, messages, latestEngineeringContext, activeWorkspaceSourceMessageId, activeWorkspaceTab, registryArtifacts, workspaceDeliverables],
  );

  const hasActivePipeline = useMemo(
    () => messages.some(hasEngineeringDashboardContent),
    [messages],
  );

  const handleCloseEngineeringWorkspace = useCallback(() => {
    setCurrentWorkspaceExpandLevel('collapsed');
  }, [setCurrentWorkspaceExpandLevel]);

  // Which variant of each edited turn is on screen, per session. Abandoned
  // branches stay in `messages`; this only decides what is currently read.
  const [activeBranchesBySession, setActiveBranchesBySession] = useState<Record<string, ActiveBranchMap>>({});
  const activeBranchesRef = useRef(activeBranchesBySession);
  activeBranchesRef.current = activeBranchesBySession;

  const getActiveBranches = useCallback(
    (sessionId: string | null): ActiveBranchMap => activeBranchesRef.current[sessionId || DRAFT_BRANCH_KEY] || {},
    [],
  );

  const selectBranch = useCallback((sessionId: string | null, groupId: string, branchId: string) => {
    const key = sessionId || DRAFT_BRANCH_KEY;
    setActiveBranchesBySession(previous => ({
      ...previous,
      [key]: { ...(previous[key] || {}), [groupId]: branchId },
    }));
  }, []);

  const handleSelectBranch = useCallback((groupId: string, branchId: string) => {
    selectBranch(currentSessionIdRef.current, groupId, branchId);
  }, [selectBranch]);

  const activePathMessages = useMemo(
    () => selectActivePath(messages, activeBranchesBySession[currentSessionId || DRAFT_BRANCH_KEY] || {}),
    [activeBranchesBySession, currentSessionId, messages],
  );

  const focusSessionSearch = useCallback(() => {
    if (!isSidebarOpen) setIsSidebarOpen(true);
    // The sidebar may be mounting; focus after it paints.
    requestAnimationFrame(() => {
      const input = document.querySelector<HTMLInputElement>('[data-session-search]');
      input?.focus();
      input?.select();
    });
  }, [isSidebarOpen]);

  useEffect(() => {
    setActiveWorkspaceSourceMessageId(null);
    useFilePreviewStore.getState().close();
  }, [currentSessionId]);

  const handleOpenWorkspace = useCallback((messageId: string, tab: WorkspaceTab = 'requirement') => {
    useFilePreviewStore.getState().close();
    setActiveWorkspaceSourceMessageId(messageId);
    setCurrentWorkspaceTab(tab);
    setCurrentWorkspaceExpandLevel('full');
  }, [setCurrentWorkspaceExpandLevel, setCurrentWorkspaceTab]);

  useEffect(() => {
    const applyTheme = () => {
      const savedTheme = getItem('ui_theme') || 'Dark';
      let isDark = false;
      if (savedTheme === 'Dark') isDark = true;
      else if (savedTheme === 'Light') isDark = false;
      else isDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;

      if (isDark) {
        document.documentElement.classList.add('dark');
        document.documentElement.style.backgroundColor = '#0d1117';
        document.documentElement.style.colorScheme = 'dark';
      } else {
        document.documentElement.classList.remove('dark');
        document.documentElement.style.backgroundColor = '#f1f5f9';
        document.documentElement.style.colorScheme = 'light';
      }
    };

    applyTheme();
    window.addEventListener('settings_updated', applyTheme);
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemThemeChange = () => {
      if (getItem('ui_theme') === 'System') applyTheme();
    };
    mediaQuery.addEventListener('change', handleSystemThemeChange);
    return () => {
      window.removeEventListener('settings_updated', applyTheme);
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  }, []);

  useEffect(() => {
    if (!authUser) {
      setSessions([]);
      setMessages([]);
      setCurrentSessionId(null);
      currentSessionIdRef.current = null;
      setLoadingSessionId(null);
      setHistoryLoadingSessionId(null);
      setHistoryLoadErrorSessionId(null);
      setWorkspacePanelStateBySession({});
      pendingDeletesRef.current.clear();
      return;
    }

    void (async () => {
      try {
        const [remoteSessionsRaw, projectList] = await Promise.all([
          loadRemoteSessions(),
          loadProjects().catch((error) => {
            console.error('Failed to load projects', error);
            return [] as ProjectRecord[];
          }),
        ]);
        setProjects(projectList);

        const remoteSessions = normalizeSessionsForPersistence(remoteSessionsRaw);
        const localSessions = loadLocalSessionsSnapshot(storageScope);
        const enrichedLocal = await enrichSessionsFromIndexedDB(localSessions);
        let mergedSessions = mergeRemoteAndLocalSessions(remoteSessions, enrichedLocal);
        const projectMembership = await buildProjectSessionMembership(projectList);
        mergedSessions = applyProjectMembershipToSessions(mergedSessions, projectMembership);

        // Recover orphaned plan reviews for interrupted streaming sessions
        mergedSessions = await recoverOrphanedPlanReviews(mergedSessions);

        const lastActiveId = getScopedLastActiveSessionId(getBrowserStorage(), storageScope);
        let candidate = resolveRestoredSession(
          mergedSessions,
          null,
          lastActiveId,
        );

        // The list response contains metadata only. Hydrate just the restored
        // conversation when it is not already available from IndexedDB.
        // Nothing cached, or something cached that the stream never finished
        // writing. The second half used to be missing here while
        // `handleSelectSession` had it, so a half-written turn survived every
        // reload — and a reload is how most people come back to a conversation.
        if (candidate && shouldHydrateRestoredSession(candidate)) {
          try {
            const detail = await loadRemoteSession(candidate.id);
            if (detail) {
              candidate = mergeRemoteAndLocalSessions([detail], [candidate])[0] || candidate;
              mergedSessions = mergedSessions.map((session) =>
                session.id === candidate!.id ? candidate! : session,
              );
            }
          } catch (error) {
            console.error('Failed to restore conversation detail', error);
            setHistoryLoadErrorSessionId(candidate.id);
          }
        }

        setSessions(mergedSessions);
        sessionsRef.current = mergedSessions;
        saveScopedChatSessions(getBrowserStorage(), storageScope, mergedSessions);

        if (candidate) {
          setCurrentSessionId(candidate.id);
          currentSessionIdRef.current = candidate.id;
          setMessages(candidate.messages);
          messagesRef.current = candidate.messages;
          setLoadingSessionId(candidate.isStreaming ? candidate.id : null);
          if (candidate.projectId) {
            const project = projectList.find((p) => p.id === candidate.projectId);
            setSidebarView('project');
            setActiveProjectId(candidate.projectId);
            setActiveProjectName(project?.name || '');
            setProjectSessions(mergedSessions.filter((session) => session.projectId === candidate.projectId));
          } else {
            setSidebarView('main');
            setActiveProjectId(null);
            setActiveProjectName('');
            setProjectSessions([]);
          }
        } else {
          setCurrentSessionId(null);
          setMessages([]);
          setLoadingSessionId(null);
          setSidebarView('main');
          setActiveProjectId(null);
          setActiveProjectName('');
          setProjectSessions([]);
        }
      } catch (error) {
        console.error('Failed to load remote sessions', error);
      }
    })();
  }, [authUser, storageScope]);

  useEffect(() => {
    const handleConversationsPurged = (event: Event) => {
      const detail = (event as CustomEvent<{ startMs?: number; endMs?: number }>).detail || {};
      if (typeof detail.startMs !== 'number' || typeof detail.endMs !== 'number') {
        return;
      }

      const storage = getBrowserStorage();
      purgeStoredChatSessionsByDateRange(storage, detail.startMs, detail.endMs);
      const nextSessions = loadLocalSessionsSnapshot(storageScope);
      setSessions(nextSessions);
      sessionsRef.current = nextSessions;

      const activeSession = currentSessionIdRef.current
        ? nextSessions.find((session) => session.id === currentSessionIdRef.current)
        : null;
      if (activeSession) {
        setMessages(activeSession.messages);
      } else {
        setCurrentSessionId(null);
        currentSessionIdRef.current = null;
        setMessages([]);
        setScopedLastActiveSessionId(storage, storageScope, null);
      }
    };

    window.addEventListener('realplc_conversations_purged', handleConversationsPurged);
    return () => window.removeEventListener('realplc_conversations_purged', handleConversationsPurged);
  }, [storageScope]);

  const saveToStorage = useCallback((newSessions: ChatSession[]) => {
    saveScopedChatSessions(getBrowserStorage(), storageScope, newSessions);
  }, [storageScope]);

  const hasStreamingSessions = sessions.some((session) => session.isStreaming);

  useEffect(() => {
    if (!authUser || !hasStreamingSessions) {
      return;
    }

    const refreshStreamingSessions = async () => {
      try {
        // Everything this tab was generating at any point during the tick is off
        // limits for the whole tick.
        //
        // The guards below used to read the refs directly, but they run after
        // two awaits — so a tick that starts while a turn is streaming lands
        // after that turn has finished and cleared the refs. The guard then
        // passes, and the finished turn on screen is replaced by the snapshot
        // this same tick fetched from the server while the turn was still
        // half-written. Sampled before the fetches and again after them, so a
        // generation that started mid-tick is covered too.
        const generatedHereDuringTick = new Set<string>();
        const sampleGeneratingHere = () => {
          if (generatingSessionRef.current) {
            generatedHereDuringTick.add(generatingSessionRef.current);
          }
          for (const id of abortControllersRef.current.keys()) {
            generatedHereDuringTick.add(id);
          }
        };
        sampleGeneratingHere();
        // A session whose TIA target the page wrote after this point keeps it:
        // the copies fetched below may predate what the server stored.
        const fetchedAfter = serverTiaTargetWrites.mark();

        const summarySessions = normalizeSessionsForPersistence(await loadRemoteSessions());
        sampleGeneratingHere();

        // The list carries no message bodies, which is what keeps this
        // three-second tick cheap no matter how long the history is. Only the
        // conversations the server still reports as streaming need their
        // content pulled, and only when this tab is not the one generating them
        // — a locally generating session is already being fed by its own SSE
        // stream and its local copy is ahead of anything the server has stored.
        //
        // And only when the conversation has actually moved since the last body
        // this tab pulled. It used to pull every body every tick for as long as
        // the turn ran: measured 2026-09-16 on a live turn, forty requests a
        // minute out of the organization's sixty, one passive viewer, most of
        // them re-fetching a conversation that had not changed. The worker's
        // checkpoint moves the session row, so `updatedAt` from the list is the
        // signal — see needsBodyFetch, and the auth-core cases that keep the
        // checkpoint touching that row.
        // TA10c: a summary that just stopped streaming still has to be pulled
        // once more when this tab's own cached copy of it never saw the turn
        // end — a tab that reloaded mid-turn restores exactly that copy, and
        // its marker below is undefined (a fresh `useRef`). But "no marker"
        // is also true of every already-finished conversation this tab has
        // simply never opened, so `needsBodyFetch` needs `localCopyStreaming`
        // to tell the two apart in that one case — a cost regression found in
        // review otherwise fetched every never-seen session's body in one
        // tick (2026-09-29).
        // I-1: the marker records whether the summary was streaming *at* the
        // `updatedAt` this tab last fetched, not just the number — a crashed
        // generation can clear `is_streaming` without moving `updated_at`
        // (`reconcileInterruptedGenerations`, auth-core), and only that pairing
        // tells the two states apart.
        const hydrated = new Map<string, ChatSession>();
        for (const session of summarySessions) {
          const localSession = sessionsRef.current.find((s) => s.id === session.id);
          if (!needsBodyFetch(
            session,
            lastHydratedUpdatedAtRef.current.get(session.id),
            generatedHereDuringTick.has(session.id),
            isLocalCopyStreaming(localSession),
          )) continue;
          try {
            const full = await loadRemoteSession(session.id);
            if (full) {
              hydrated.set(full.id, full);
              lastHydratedUpdatedAtRef.current.set(session.id, {
                updatedAt: session.updatedAt,
                streaming: Boolean(session.isStreaming),
              });
            }
          } catch {
            // Leave the summary in place; the next tick tries again.
          }
          sampleGeneratingHere();
        }

        const remoteSessions = summarySessions.map((session) => hydrated.get(session.id) || session);
        const remoteById = new Map(remoteSessions.map((s) => [s.id, s]));
        const localIds = new Set(sessionsRef.current.map((s) => s.id));

        // Sync data from remote without reordering
        const synced = sessionsRef.current.map((local) => {
          const remote = remoteById.get(local.id);
          const isActivelyGenerating = generatedHereDuringTick.has(local.id);
          if (!remote) return clearOrphanedStreamingFlag(local, isActivelyGenerating);
          if (isActivelyGenerating) return local;
          // The server has no notion of stage buffers, so adopting its snapshot
          // wholesale erases the step stream the user is reading.
          const remoteWithLocalSubmissions = preserveStreamedTurnRecord(
            mergeSessionSubmittedPlanReviewResults(keepMessagesAgainstSummary(remote, local), local),
            local,
          );
          // 如果本地已有生成代码但远程快照还没有，保留本地消息防止代码被清空
          const localHasOutput = sessionHasCompletedOutput(local.messages || []);
          const remoteHasOutput = sessionHasCompletedOutput(remoteWithLocalSubmissions.messages || []);
          const messages = preferLocalMessages({
            localHasOutput,
            remoteHasOutput,
            localUpdatedAt: local.updatedAt,
            remoteUpdatedAt: remote.updatedAt,
          })
            ? local.messages
            : remoteWithLocalSubmissions.messages;
          // The server persists messages but not `isStreaming`, so the copy
          // adopted here always looks finished. Hand the session's own
          // liveness down to its newest turn, or a run still in repair is
          // rendered as a delivered one — deliverables and all — until the
          // next event takes them away again.
          return applySessionLivenessToTurn({
            ...local,
            projectId: local.projectId || remote.projectId,
            messages,
            updatedAt: Math.max(local.updatedAt || 0, remote.updatedAt || 0),
            isStreaming: remote.isStreaming,
            // What the server wrote into the snapshot wins over the cached copy.
            sessionConfig: serverTiaTargetWrites.adopt(fetchedAfter, local.id, local.sessionConfig, remote.sessionConfig),
          });
        });

        // Prepend new remote sessions not yet known locally
        const activeScopedProjectId = activeProjectId;
        const newSessions = remoteSessions
          .filter((s) => !localIds.has(s.id) && !pendingDeletesRef.current.has(s.id))
          .map((session) => {
            if (
              activeScopedProjectId
              && (session.id === currentSessionIdRef.current || session.id === generatingSessionRef.current)
              && !session.projectId
            ) {
              return { ...session, projectId: activeScopedProjectId };
            }
            return session;
          });
        const finalSessions = newSessions.length > 0
          ? [...newSessions, ...synced]
          : synced;

        setSessions(finalSessions);
        sessionsRef.current = finalSessions;
        saveToStorage(finalSessions);

        const activeSessionId = currentSessionIdRef.current;
        const activeSession = activeSessionId
          ? finalSessions.find((session) => session.id === activeSessionId)
          : null;

        const activeSessionIsGenerating = Boolean(
          activeSessionId && generatedHereDuringTick.has(activeSessionId),
        );

        if (activeSession && !activeSessionIsGenerating) {
          setMessages(activeSession.messages);
          setLoadingSessionId(activeSession.isStreaming ? activeSession.id : null);
        } else if (activeSession) {
          setLoadingSessionId(activeSession.isStreaming ? activeSession.id : null);
        }
        return true;
      } catch (error) {
        console.error('Failed to refresh streaming sessions', error);
        return false;
      }
    };

    // Self-scheduling rather than a fixed interval. This tick is the only thing
    // that clears a stale isStreaming flag, and that flag is the only thing
    // keeping the tick alive — so retrying a rate-limited tick every three
    // seconds deadlocks the tab. Backing off lets it recover.
    let stopped = false;
    let consecutiveFailures = 0;
    let timer: number | null = null;

    const tick = async () => {
      const succeeded = await refreshStreamingSessions();
      consecutiveFailures = succeeded ? 0 : consecutiveFailures + 1;
      if (stopped) return;
      // Watching our own generation does not need the cadence used for watching
      // another tab's — this tick skips hydrating anything we are generating.
      const watchingOnlyLocalStreams = sessionsRef.current
        .filter((session) => session.isStreaming)
        .every((session) => session.id === generatingSessionRef.current
          || abortControllersRef.current.has(session.id));
      timer = window.setTimeout(
        () => void tick(),
        streamingPollDelayMs(consecutiveFailures, { watchingOnlyLocalStreams, pageHidden: document.visibilityState === 'hidden' }),
      );
    };

    void tick();
    return () => {
      stopped = true;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [activeProjectId, authUser, hasStreamingSessions, saveToStorage, serverTiaTargetWrites]);

  const saveSessions = useCallback((newSessions: ChatSession[]) => {
    // 这个 ref 是所有"当前会话列表"读取点的真实来源：轮询合并、流式写回、
    // projectSessions 重算都读它。漏掉这一行，删除只改了 React state，
    // 下一次流式写回会把 ref 里那条已删会话原样搬回列表和 localStorage。
    sessionsRef.current = newSessions;
    setSessions(newSessions);
    saveToStorage(newSessions);
  }, [saveToStorage]);

  /**
   * What the server stored of a session's TIA target (TIA 根治 P2, T2b), into
   * the page's copy of the session and so into IndexedDB; the open
   * conversation's TIA target follows the snapshot. A stream's later writes
   * spread the session they find in `sessionsRef`, so they keep it.
   */
  const sessionSnapshotWriter = useMemo(
    () => createSessionSnapshotWriter({ read: () => sessionsRef.current, save: saveSessions, writes: serverTiaTargetWrites }),
    [saveSessions, serverTiaTargetWrites],
  );

  // The project picker (T5) announces what a request of its own stored.
  useEffect(() => listenForSessionTiaTargetStored(sessionSnapshotWriter), [sessionSnapshotWriter]);

  // A validation wrote a conversation's project reference, a project it
  // created (E11 ③; T11): the open conversation reads it again.
  useEffect(() => onSessionTiaTargetWritten(({ sessionId }) => {
    const projectId = sessionsRef.current.find((item) => item.id === sessionId)?.projectId || activeProjectId;
    void reloadSessionTiaProjectRef(sessionId, projectId);
  }), [activeProjectId]);

  const updateSessionMessages = useCallback((sessionId: string, updatedMessages: Message[], status?: string, isStreaming?: boolean) => {
    setSessions(prev => {
      const now = Date.now();
      const updated = prev.map(session =>
        session.id === sessionId
          ? {
              ...session,
              messages: updatedMessages.length > 0 ? updatedMessages : session.messages,
              updatedAt: now,
              status: status || session.status,
              isStreaming: isStreaming !== undefined ? isStreaming : session.isStreaming,
              lastStreamingActivityAt: isStreaming ? now : session.lastStreamingActivityAt,
            }
          : session
      );
      sessionsRef.current = updated;
      saveToStorage(updated);
      return updated;
    });
  }, [saveToStorage]);

  const markPlanReviewSubmitted = useCallback((
    messageId: string,
    action: NonNullable<Message['planReview']>['reviewResult'],
    approvalSource?: NonNullable<Message['planReview']>['approvalSource'],
    acceptedSignals?: string[],
  ) => {
    if (!action) return;
    const sessionId = currentSessionIdRef.current;
    if (!sessionId) return;

    // One stamp for every copy of the message, so the visible list and the
    // stored session agree on when the plan was answered.
    const answeredAt = Date.now();

    // The proposed signals the engineer ticked stay on a plan they decided by
    // hand, here and on the history's copy below, for a card drawn again from
    // the stored message before the worker republishes the plan. Never for the
    // countdown's approval, which accepts none.
    const keepsTicks = (plan: NonNullable<Message['planReview']>) =>
      streamState.keepsTickedSignals(plan, action, approvalSource, acceptedSignals);

    const applyReviewResult = (items: Message[]) => items.map((message) => {
      if (message.id !== messageId) return message;

      const planReview = message.planReview && !message.planReview.reviewResult
        ? {
            ...message.planReview,
            reviewResult: action,
            approvalSource: approvalSource || message.planReview.approvalSource,
            ...(keepsTicks(message.planReview) ? { acceptedSignals } : {}),
          }
        : message.planReview;
      const planProcessHistory = streamState.applyPlanReviewResultToHistory(message.planProcessHistory, action, approvalSource, acceptedSignals);
      // The step the turn draws comes from the stage buffer alone, so without
      // this the amber "waiting for your approval" card stays up untouched for
      // the whole round trip to the runtime and back.
      const stageBuffers = advancePlanReviewOnApproval(message.stageBuffers, answeredAt);

      if (
        planReview === message.planReview
        && planProcessHistory === message.planProcessHistory
        && stageBuffers === message.stageBuffers
      ) {
        return message;
      }

      return {
        ...message,
        planReview,
        planProcessHistory,
        stageBuffers,
      };
    });

    const visibleMessages = applyReviewResult(messagesRef.current);
    messagesRef.current = visibleMessages;
    if (currentSessionIdRef.current === sessionId) {
      setMessages(visibleMessages);
    }

    setSessions(prev => {
      const updated = prev.map(session =>
        session.id === sessionId
          ? {
              ...session,
              messages: applyReviewResult(session.messages),
            }
          : session,
      );
      sessionsRef.current = updated;
      saveToStorage(updated);
      return updated;
    });
  }, [saveToStorage]);

  /**
   * The model's background reading of a delivered turn's test cases (TA9c)
   * lands well after the turn settled and this session's own streaming
   * closure is long gone — so it goes through the same update-and-persist
   * path as any other post-delivery patch (`markPlanReviewSubmitted` above),
   * not a store of its own.
   *
   * A direct IndexedDB patch was tried here first and reverted (2026-09-29
   * second review): the in-memory copy this function now updates is exactly
   * what every other save (`saveToStorage`'s three-second tick while a
   * session streams, a send, a session switch) rewrites the whole store
   * from, so a patch that bypassed it was overwritten within moments, and a
   * separate read-then-write transaction against that same store could also
   * land an older snapshot over a newer one.
   */
  const handleTestHintsResolved = useCallback((
    sessionId: string,
    messageId: string,
    testHints: GenerationTestHints,
  ) => {
    const applyTestHints = (items: Message[]) => items.map((message) => {
      if (message.id !== messageId) return message;
      return {
        ...message,
        generationMetrics: { ...(message.generationMetrics || {}), testHints },
      };
    });

    if (currentSessionIdRef.current === sessionId) {
      const visibleMessages = applyTestHints(messagesRef.current);
      messagesRef.current = visibleMessages;
      setMessages(visibleMessages);
    }

    setSessions(prev => {
      const updated = prev.map(session =>
        session.id === sessionId
          ? { ...session, messages: applyTestHints(session.messages) }
          : session,
      );
      sessionsRef.current = updated;
      saveToStorage(updated);
      return updated;
    });
  }, [saveToStorage]);

  const markSessionStopped = useCallback((sessionId: string) => {
    const stoppedAt = Date.now();
    let visibleMessages: Message[] | null = null;

    setSessions(prev => {
      const updated = prev.map(session => {
        if (session.id !== sessionId) return session;

        const messages = session.messages.map(message => {
          if (message.role !== 'assistant' || !message.isStreaming) return message;

          return {
            ...message,
            isStreaming: false,
            progressState: {
              ...(message.progressState || {}),
              phase: 'failed',
              label: language === 'English' ? 'Generation stopped' : '生成已停止',
              updatedAt: stoppedAt,
            },
          };
        });

        if (currentSessionIdRef.current === sessionId) {
          visibleMessages = messages;
        }

        return {
          ...session,
          messages,
          isStreaming: false,
          updatedAt: stoppedAt,
        };
      });

      sessionsRef.current = updated;
      saveToStorage(updated);
      return updated;
    });

    if (visibleMessages) {
      setMessages(visibleMessages);
      messagesRef.current = visibleMessages;
    }
  }, [saveToStorage, language]);

  const handleToggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const handleGoHome = () => setView('landing');
  const closeSidebarOnCompactViewport = () => {
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches) {
      setIsSidebarOpen(false);
    }
  };

  const handleNewChat = () => {
    setCurrentSessionId(null);
    currentSessionIdRef.current = null;
    setMessages([]);
    messagesRef.current = [];
    setLoadingSessionId(null);
    setHistoryLoadingSessionId(null);
    setHistoryLoadErrorSessionId(null);
    setWorkspacePanelStateBySession(prev => resetWorkspacePanelState(prev, null));
    setScopedLastActiveSessionId(getBrowserStorage(), storageScope, null);
    // If inside a project sub-view, return to main view
    if (sidebarView === 'project') {
      setSidebarView('main');
      setActiveProjectId(null);
      setActiveProjectName('');
      setProjectSessions([]);
    }
    closeSidebarOnCompactViewport();
  };

  const handleNewProjectChat = useCallback(() => {
    if (!activeProjectId) return;
    setCurrentSessionId(null);
    currentSessionIdRef.current = null;
    setMessages([]);
    messagesRef.current = [];
    setLoadingSessionId(null);
    setWorkspacePanelStateBySession(prev => resetWorkspacePanelState(prev, null));
    setScopedLastActiveSessionId(getBrowserStorage(), storageScope, null);
  }, [activeProjectId, storageScope]);

  const handleOpenCreateProject = useCallback(() => {
    setIsCreateProjectModalOpen(true);
  }, []);

  const handleCreateProject = useCallback(async (name: string) => {
    const project = await createProject(name);
    const updated = await loadProjects();
    setProjects(updated);

    // Enter project sub-view (no auto-created session — sessions are created on first message)
    setActiveProjectId(project.id);
    setActiveProjectName(project.name);
    setProjectSessions([]);
    setSidebarView('project');
    // Reset chat area for new project
    setCurrentSessionId(null);
    currentSessionIdRef.current = null;
    setMessages([]);
    messagesRef.current = [];
    setLoadingSessionId(null);
    setWorkspacePanelStateBySession(prev => resetWorkspacePanelState(prev, null));
    setScopedLastActiveSessionId(getBrowserStorage(), storageScope, null);
  }, [saveToStorage, storageScope]);

  const handleEnterProject = useCallback(async (projectId: string) => {
    const project = projects.find((p) => p.id === projectId);
    if (!project) return;

    setActiveProjectId(projectId);
    setActiveProjectName(project.name);

    let loadedProjectSessions: ChatSession[] | null = null;

    // Load project workspace to get sessions
    try {
      const fetchedAfter = serverTiaTargetWrites.mark();
      const workspace = await loadProjectWorkspace(projectId);
      // Merge workspace sessions with local sessions
      const localProjectSessions = sessionsRef.current.filter(
        (s) => s.projectId === projectId,
      );
      const workspaceSessionIds = new Set(workspace.sessions.map((s) => s.id));
      const localOnly = localProjectSessions.filter(
        (s) => !workspaceSessionIds.has(s.id),
      );

      const merged: ChatSession[] = [
        ...workspace.sessions.map((s) => {
          const local = localProjectSessions.find(entry => entry.id === s.id);
          return {
            id: s.id,
            projectId: s.projectId || projectId,
            title: s.title,
            messages: local?.messages || [],
            updatedAt: s.updatedAt,
            status: s.status,
            // The sidebar names the target platform from this config, so dropping
            // it would leave the row showing the raw stored status string.
            sessionConfig: s.sessionConfig || local?.sessionConfig,
            isStreaming: s.isStreaming,
            isStarred: s.isStarred,
          };
        }),
        ...localOnly,
      ].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));

      loadedProjectSessions = merged;
      setProjectSessions(merged);
      setSessions(prev => {
        const byId = new Map(prev.map(session => [session.id, session]));
        for (const projectSession of merged) {
          const existing = byId.get(projectSession.id);
          byId.set(projectSession.id, existing
            ? {
                ...existing,
                projectId: existing.projectId || projectId,
                sessionConfig: serverTiaTargetWrites.adopt(
                  fetchedAfter,
                  existing.id,
                  existing.sessionConfig,
                  projectSession.sessionConfig,
                ),
              }
            : projectSession,
          );
        }
        const updated = Array.from(byId.values()).sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
        sessionsRef.current = updated;
        saveToStorage(updated);
        return updated;
      });
    } catch {
      // Fallback to local sessions
      const local = sessionsRef.current.filter((s) => s.projectId === projectId);
      loadedProjectSessions = local;
      setProjectSessions(local);
    }

    // Select the most recent session if there is one, otherwise stay
    const projectSessionList = loadedProjectSessions || sessionsRef.current.filter(
      (s) => s.projectId === projectId,
    );
    if (projectSessionList.length > 0) {
      const latest = projectSessionList.sort(
        (a, b) => (b.updatedAt || 0) - (a.updatedAt || 0),
      )[0];
      const fullLatest = sessionsRef.current.find((session) => session.id === latest.id) || latest;
      setCurrentSessionId(fullLatest.id);
      currentSessionIdRef.current = fullLatest.id;
      setMessages(fullLatest.messages);
      setScopedLastActiveSessionId(getBrowserStorage(), storageScope, fullLatest.id);
    } else {
      // No existing sessions for this project — stay on empty chat area
      setCurrentSessionId(null);
      currentSessionIdRef.current = null;
      setMessages([]);
      messagesRef.current = [];
      setLoadingSessionId(null);
      setScopedLastActiveSessionId(getBrowserStorage(), storageScope, null);
    }

    setSidebarView('project');
  }, [projects, saveToStorage, serverTiaTargetWrites, storageScope]);

  const handleBackFromProject = useCallback(() => {
    setSidebarView('main');
    setActiveProjectId(null);
    setActiveProjectName('');
    setProjectSessions([]);
  }, []);

  const handleDeleteProject = useCallback(async (projectId: string) => {
    try {
      await deleteProject(projectId);
    } catch (err) {
      console.error('Failed to delete project', err);
    }
    setProjects((prev) => prev.filter((p) => p.id !== projectId));
    // Remove project-scoped sessions locally
    const nextSessions = sessionsRef.current.filter(
      (s) => s.projectId !== projectId,
    );
    sessionsRef.current = nextSessions;
    saveToStorage(nextSessions);
    setSessions(nextSessions);
    // If we were viewing this project, go back
    if (activeProjectId === projectId) {
      handleBackFromProject();
      if (currentSessionIdRef.current && nextSessions.find(s => s.id === currentSessionIdRef.current)) {
        // keep current session if it was NOT in the deleted project
        const unchanged = nextSessions.find(s => s.id === currentSessionIdRef.current);
        if (unchanged) {
          setMessages(unchanged.messages);
        } else {
          setCurrentSessionId(null);
          currentSessionIdRef.current = null;
          setMessages([]);
        }
      } else {
        setCurrentSessionId(null);
        currentSessionIdRef.current = null;
        setMessages([]);
      }
    }
  }, [activeProjectId, saveToStorage, handleBackFromProject]);

  const handleRenameProject = useCallback(async (projectId: string, name: string) => {
    try {
      await updateProject(projectId, { name });
    } catch (err) {
      console.error('Failed to rename project', err);
    }
    setProjects((prev) =>
      prev.map((p) => (p.id === projectId ? { ...p, name } : p)),
    );
    if (activeProjectId === projectId) {
      setActiveProjectName(name);
    }
  }, [activeProjectId]);

  const markSessionViewed = useCallback((id: string) => {
    setViewedSessionIds(prev => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      try { localStorage.setItem('realplc_viewed_sessions', JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
  }, []);

  /**
   * Pull one conversation's messages from the server.
   *
   * The conversation list is a summary and carries no message bodies, so a
   * session that has never been opened on this device arrives empty. IndexedDB
   * covers the ones opened before; this covers the rest.
   */
  const hydrateSessionMessages = useCallback(async (id: string) => {
    if (!authUser || hydratedSessionIdsRef.current.has(id)) return;
    hydratedSessionIdsRef.current.add(id);

    let full: ChatSession | null = null;
    const fetchedAfter = serverTiaTargetWrites.mark();
    try {
      full = await loadRemoteSession(id);
    } catch (error) {
      console.error('Failed to load conversation messages', error);
      hydratedSessionIdsRef.current.delete(id);
      return;
    }
    if (!full?.messages?.length) return;

    const [normalized] = normalizeSessionsForPersistence([full]);
    setSessions((prev) => {
      const updated = prev.map((session) => (
        session.id === id
          ? {
              ...session,
              messages: normalized.messages,
              sessionConfig: serverTiaTargetWrites.adopt(fetchedAfter, id, session.sessionConfig, normalized.sessionConfig),
            }
          : session
      ));
      sessionsRef.current = updated;
      saveToStorage(updated);
      return updated;
    });

    if (currentSessionIdRef.current === id) {
      setMessages(normalized.messages);
      messagesRef.current = normalized.messages;
    }
  }, [authUser, saveToStorage, serverTiaTargetWrites]);

  useEffect(() => {
    const refresh = (event: Event) => {
      const id = (event as CustomEvent<string>).detail;
      if (!sessionsRef.current.some(session => session.id === id)) return;
      hydratedSessionIdsRef.current.delete(id);
      void hydrateSessionMessages(id);
    };
    window.addEventListener('realplc:refresh-recovered-session', refresh);
    return () => window.removeEventListener('realplc:refresh-recovered-session', refresh);
  }, [hydrateSessionMessages]);

  const handleSelectSession = async (id: string) => {
    const session = sessionsRef.current.find(item => item.id === id);
    if (!session) return;
    // A session restored from the cache is only trustworthy if the stream that
    // wrote it finished. One that still says it is streaming was interrupted --
    // the browser opens cached conversations without re-fetching, so whatever
    // had not arrived stays missing for good. Refetching repairs the row too:
    // `hydrateSessionMessages` writes the server's copy back to storage.
    const generatingHere = generatingSessionRef.current === id
      || abortControllersRef.current.has(id);
    if (generatingHere ? !session.messages?.length : shouldHydrateRestoredSession(session)) {
      void hydrateSessionMessages(id);
    }
    // Mark as viewed — green dot disappears when user opens the session
    markSessionViewed(id);
    if (session.projectId) {
      const project = projects.find((p) => p.id === session.projectId);
      setSidebarView('project');
      setActiveProjectId(session.projectId);
      setActiveProjectName(project?.name || activeProjectName);
      setProjectSessions(sessionsRef.current.filter((s) => s.projectId === session.projectId));
    }
    setCurrentSessionId(id);
    currentSessionIdRef.current = id;
    setMessages(session.messages);
    setLoadingSessionId(session.isStreaming ? id : null);
    setHistoryLoadErrorSessionId(null);
    setScopedLastActiveSessionId(getBrowserStorage(), storageScope, id);
    closeSidebarOnCompactViewport();

    if (session.messages.length > 0) return;

    setHistoryLoadingSessionId(id);
    try {
      const detail = await loadRemoteSession(id);
      if (!detail || pendingDeletesRef.current.has(id)) {
        throw new Error('Conversation detail was not found');
      }
      const hydrated = mergeRemoteAndLocalSessions([detail], [session])[0] || detail;
      const updated = sessionsRef.current.map((item) => item.id === id ? hydrated : item);
      sessionsRef.current = updated;
      setSessions(updated);
      saveScopedChatSessions(getBrowserStorage(), storageScope, updated);
      if (currentSessionIdRef.current === id) {
        setMessages(hydrated.messages);
        messagesRef.current = hydrated.messages;
      }
    } catch (error) {
      console.error('Failed to load conversation detail', error);
      if (currentSessionIdRef.current === id) {
        setHistoryLoadErrorSessionId(id);
      }
    } finally {
      setHistoryLoadingSessionId((current) => current === id ? null : current);
    }
  };

  const handleDeleteSession = (id: string) => {
    const remoteDeletePromise = authUser
      ? deleteRemoteSession(id).catch((error) => {
          console.error('Failed to delete remote session', error);
        })
      : null;

    const controller = abortControllersRef.current.get(id);
    if (controller) {
      controller.abort();
      abortControllersRef.current.delete(id);
    }
    const jobId = activeJobIdsRef.current.get(id);
    if (jobId) {
      activeJobIdsRef.current.delete(id);
      void cancelPlcGeneration(jobId).catch((error) => {
        console.error('Failed to cancel generation job while deleting session', error);
      });
    }
    if (generatingSessionRef.current === id) generatingSessionRef.current = null;
    pendingDeletesRef.current.add(id);

    const nextSessions = sessionsRef.current.filter(session => session.id !== id);
    saveSessions(nextSessions);
    // 项目子视图渲染的是 projectSessions，不是 sessions。不同步这一份，
    // 项目里删掉的会话会一直挂在侧边栏上，直到刷新页面才重新推导。
    setProjectSessions(prev => prev.filter(session => session.id !== id));
    void remoteDeletePromise;
    if (currentSessionIdRef.current === id) {
      setCurrentSessionId(null);
      setMessages([]);
      setScopedLastActiveSessionId(getBrowserStorage(), storageScope, null);
    }
  };

  /**
   * 写入会话标题，同时记下这个标题是谁起的。
   * titleSource 随会话一起持久化，'user' 之后自动改写永不触碰该会话。
   */
  const applySessionTitle = useCallback((
    id: string,
    title: string,
    titleSource: TitleSource = 'auto',
  ) => {
    setSessions(prev => {
      const updated = prev.map(session =>
        session.id === id ? { ...session, title, titleSource } : session
      );
      sessionsRef.current = updated;
      saveToStorage(updated);
      return updated;
    });
    if (authUser) {
      void updateRemoteSession(id, { title }).catch((error) => {
        console.error('Failed to rename remote session', error);
      });
    }
  }, [authUser, saveToStorage]);

  const handleRenameSession = useCallback((id: string, title: string) => {
    const normalizedTitle = title.replace(/\s+/g, ' ').trim();
    if (!normalizedTitle) return;
    applySessionTitle(id, normalizedTitle, 'user');
  }, [applySessionTitle]);

  // Rename a session to the project name the requirement analysis produced,
  // once some turn has produced one.
  const autoGenerateSessionTitle = useCallback((sessionId: string) => {
    if (titleGeneratedRef.current.has(sessionId)) return;
    const session = sessionsRef.current.find(s => s.id === sessionId);
    if (!session) return;

    const analysedName = resolveAutoSessionTitle(session);
    // Nothing to rename to yet, and deliberately not marked as done: the turn
    // that analyses the requirement can be a later one than this, and the
    // initial title is a better placeholder than anything sliced out of the
    // prompt here -- it keeps 30 characters and the trailing dots that let it
    // be upgraded later. See utils/sessionTitle.
    if (!analysedName) return;

    titleGeneratedRef.current.add(sessionId);
    applySessionTitle(sessionId, analysedName);
  }, [applySessionTitle]);

  const handleStarSession = useCallback((id: string) => {
    setSessions(prev => {
      const updated = prev.map(session => {
        if (session.id !== id) return session;
        return { ...session, isStarred: !session.isStarred };
      });
      sessionsRef.current = updated;
      saveToStorage(updated);

      const toggled = updated.find(s => s.id === id);
      if (authUser && toggled) {
        void updateRemoteSession(id, { isStarred: toggled.isStarred }).catch((error) => {
          console.error('Failed to star remote session', error);
        });
      }

      return updated;
    });
  }, [authUser, saveToStorage]);

  /**
   * 这一会话正在跑的那个 job 的 id。
   *
   * `activeJobIdsRef` 是内存 ref，流一结束就被 finally 清掉，刷新页面更是直接为空。
   * 所以取不到时回落到消息自己带的 `generationMetrics.requestId`——它随消息落库，
   * 刷新、切会话、重连都还在。
   */
  const resolveActiveJobId = useCallback((sessionId: string): string | undefined => {
    const fromRef = activeJobIdsRef.current.get(sessionId);
    if (fromRef) return fromRef;
    const session = sessionsRef.current.find(item => item.id === sessionId);
    const streaming = [...(session?.messages || [])].reverse().find(
      message => message.role === 'assistant' && message.isStreaming,
    );
    const requestId = (streaming as { generationMetrics?: { requestId?: string } } | undefined)
      ?.generationMetrics?.requestId;
    return typeof requestId === 'string' && requestId ? requestId : undefined;
  }, []);

  const handleStopGeneration = useCallback(() => {
    const sessionId = currentSessionIdRef.current;
    if (sessionId) {
      const controller = abortControllersRef.current.get(sessionId);
      if (controller) {
        controller.abort();
        abortControllersRef.current.delete(sessionId);
      }
      // 取不到 job id 就什么都不做，是这个按钮最常见的失效方式。
      //
      // 原来是 `if (jobId) { …cancel… }`，没有 else：拿不到就不发请求、不报错、不提示，
      // 然后照样把本地标成已停。而 `activeJobIdsRef` 在流结束时被 finally 清空，所以
      // **流断之后再按必然是空操作**——用户按了停止，后端一无所知，侧边栏继续转。
      // 实测：那一刻 Redis 里没有任何当天的取消键。
      const jobId = resolveActiveJobId(sessionId);
      if (jobId) {
        activeJobIdsRef.current.delete(sessionId);
        void cancelPlcGeneration(jobId).catch((error) => {
          console.error('Failed to cancel generation job', error);
        });
      } else {
        // 说出来，而不是假装停住了。本地状态仍然收口（用户要的是别再转），
        // 但不再让人以为后端也停了。
        console.warn('[stop] no job id for session', sessionId, '— backend generation was not cancelled');
      }
      if (generatingSessionRef.current === sessionId) generatingSessionRef.current = null;
      markSessionStopped(sessionId);
    }
    setLoadingSessionId(null);
  }, [markSessionStopped, resolveActiveJobId]);

  /**
   * The gateway stored the turn under a conversation ID of its own: the one
   * this page sent is someone else's, or was deleted in another tab or on
   * another device (geminiService's `serverSessionId`). The conversation goes
   * on under that one, in everything the page keeps by conversation. Sending
   * the old ID again, every turn after it reached the model with none of the
   * history, while the screen showed one conversation.
   */
  const followServerSession = useCallback((from: string, to: string) => {
    const renamed = renameSessionIn(sessionsRef.current, from, to);
    sessionsRef.current = renamed;
    saveToStorage(renamed);
    setSessions(renamed);
    setProjectSessions(prev => renameSessionIn(prev, from, to));
    if (currentSessionIdRef.current === from) {
      currentSessionIdRef.current = to;
      setCurrentSessionId(to);
      setScopedLastActiveSessionId(getBrowserStorage(), storageScope, to);
    }
    if (generatingSessionRef.current === from) generatingSessionRef.current = to;
    setLoadingSessionId(prev => (prev === from ? to : prev));
    renameMapKey(abortControllersRef.current, from, to);
    renameMapKey(activeJobIdsRef.current, from, to);
    setWorkspacePanelStateBySession(prev => renameKey(prev, from, to));
    setActiveBranchesBySession(prev => renameKey(prev, from, to));
    setViewedSessionIds(prev => {
      const next = renameInSet(prev, from, to);
      if (next !== prev) {
        try { localStorage.setItem('realplc_viewed_sessions', JSON.stringify([...next])); } catch { /* ignore */ }
      }
      return next;
    });
    useSessionTiaTargetStore.getState().renameSession(from, to);
  }, [saveToStorage, storageScope]);

  const handleGenerate = useCallback(async (
    prompt: string,
    config: GenerationConfig,
    options?: { replaceFromMessageId?: string; clarifyEcho?: ClarifyEchoOptions; resumesPlan?: boolean },
  ) => {
    if (!prompt.trim()) return;
    // Every send passes through here, and this is the only place that reads the
    // stored keys. Configs that arrive replayed (retry, clarify follow-up, a
    // session reopened after a refresh) carry the chosen provider but no
    // credentials for it, so taking them from the config meant a key entered
    // after the first send never took effect.
    let safeConfig = streamState.normalizeGenerationConfig(withCurrentProviderCredentials(config));
    // V1 P2: Pass RealPLC Agent enabled setting to backend
    if (typeof localStorage !== 'undefined') {
      const tiaEnabled = localStorage.getItem('agent_tia_enabled') !== 'false';
      safeConfig = { ...safeConfig, tiaAgentEnabled: tiaEnabled };
    }
    const sessionProjectId = sidebarView === 'project'
      ? activeProjectId
      : safeConfig.projectId || currentSession?.projectId || null;
    if (sessionProjectId && safeConfig.projectId !== sessionProjectId) {
      safeConfig = { ...safeConfig, projectId: sessionProjectId };
    }

    // ── "继续执行" intent detection: if user types "continue" and there's a timed-out plan ──
    if (asksToResumePlan(prompt) && !safeConfig.preApprovedPlan) {
      const sessionId = currentSessionIdRef.current;
      const currentMessages = sessionId
        ? (sessionsRef.current.find(s => s.id === sessionId)?.messages || messagesRef.current)
        : messagesRef.current;
      const timedOutPlanMsg = [...currentMessages].reverse().find(
        m => m.role === 'assistant' && m.planReview?.reviewResult === 'timed_out',
      );
      if (timedOutPlanMsg?.planReview?.subtasks?.length) {
        safeConfig = { ...safeConfig, preApprovedPlan: timedOutPlanMsg.planReview as unknown as Record<string, unknown> };
      }
    }
    let sessionId = currentSessionIdRef.current;

    if (sessionId && abortControllersRef.current.has(sessionId)) {
      abortControllersRef.current.get(sessionId)?.abort();
    }

    const userMessageTimestamp = Date.now();
    const assistantMessageTimestamp = userMessageTimestamp + 1;
    const userMessage: Message = {
      id: userMessageTimestamp.toString(),
      role: 'user',
      content: prompt,
      timestamp: userMessageTimestamp,
      attachments: safeConfig.attachments,
      clarifyEcho: options?.clarifyEcho ? true : undefined,
      // The app wrote this prompt, not the user. It still goes to the model —
      // the answer needs its antecedent — but it is not drawn as something the
      // user said.
      appComposed: safeConfig.isValidationExplanation ? true : undefined,
      // 「继续执行」 sent the plan's summary for the user: drawn as one line, not as their words.
      resumesApprovedPlan: options?.resumesPlan ? true : undefined,
    };
    const assistantMessageId = (Date.now() + 1).toString();
    const initialAssistantMsg: Message = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: assistantMessageTimestamp,
      routingMode: safeConfig.routingMode === GenerationMode.THINKING ? GenerationMode.THINKING : GenerationMode.FAST,
      progress: 1,
      progressState: streamState.buildClientProgressState(
        language,
        'queued',
        '已提交，正在建立生成任务...',
        'Submitted, preparing generation...',
        1,
      ),
      isStreaming: true,
    };

    const historySnapshot = getLatestHistorySnapshot(sessionId, sessionsRef.current, messagesRef.current);
    // Editing an earlier prompt opens a new branch beside the old one. Nothing is
    // deleted: the programs the previous wording produced stay reachable by
    // switching variants, while the model only ever sees one coherent path.
    const prepared = options?.replaceFromMessageId
      ? prepareBranchForEdit(historySnapshot, options.replaceFromMessageId)
      : null;
    const storedBeforeRaw = prepared ? prepared.messages : historySnapshot;
    // 确认卡片的答案盖回提问的那条消息。卡片的选中状态是组件本地 state，刷新
    // 就丢了——不落在消息上，这轮到底选了什么就再也查不到。
    const clarifyEcho = options?.clarifyEcho;
    const storedBefore = clarifyEcho
      ? storedBeforeRaw.map(message => (
        message.id === clarifyEcho.answeredMessageId
          ? { ...message, clarifyAnswers: clarifyEcho.answers }
          : message
      ))
      : storedBeforeRaw;

    if (prepared) {
      userMessage.variantGroupId = prepared.variantGroupId;
      userMessage.branchPath = prepared.newBranchPath;
      initialAssistantMsg.branchPath = prepared.newBranchPath;
      selectBranch(sessionId, prepared.variantGroupId, prepared.newBranchPath[prepared.newBranchPath.length - 1]);
    }

    const sessionInitialMessages = [...storedBefore, userMessage, initialAssistantMsg];
    // The model reads the visible path only — abandoned branches are history the
    // user chose to set aside, not context.
    const currentMessagesSnapshot = selectActivePath(
      storedBefore,
      prepared
        ? { ...getActiveBranches(sessionId), [prepared.variantGroupId]: prepared.newBranchPath[prepared.newBranchPath.length - 1] }
        : getActiveBranches(sessionId),
    );
    setMessages(sessionInitialMessages);

    const sessionStatus = formatSessionStatusLabel(safeConfig);
    // The turn's part in the session's TIA target (TIA 根治 P2, T2b): the work
    // mode every turn, and the user's project choice until the server stores it.
    const turnTiaTarget = beginTurnTiaTarget({
      sessionId,
      config: safeConfig,
      tiaWorkMode: getSavedTiaWorkMode(),
      writer: sessionSnapshotWriter,
      writes: serverTiaTargetWrites,
    });
    const previousSessionConfig = sessionId
      ? sessionsRef.current.find(session => session.id === sessionId)?.sessionConfig
      : undefined;
    const sessionConfig = streamState.createSessionConfigSnapshot(turnTiaTarget.stored, previousSessionConfig);

    if (!sessionId) {
      sessionId = Date.now().toString();
      setCurrentSessionId(sessionId);
      currentSessionIdRef.current = sessionId;
      turnTiaTarget.named(sessionId);
      setScopedLastActiveSessionId(getBrowserStorage(), storageScope, sessionId);
      setWorkspacePanelStateBySession(prev => ({
        ...prev,
        [sessionId]: getWorkspacePanelState(prev, null),
      }));
      const newSession: ChatSession = {
        id: sessionId,
        projectId: sessionProjectId || undefined,
        title: createInitialSessionTitle(prompt),
        // 首句截断只是占位，等需求分析产出 projectName 后会被自动替换。
        titleSource: 'auto',
        messages: sessionInitialMessages,
        updatedAt: Date.now(),
        status: sessionStatus,
        isStreaming: true,
        sessionConfig,
      };
      const updatedSessions = [newSession, ...sessionsRef.current];
      sessionsRef.current = updatedSessions;
      saveToStorage(updatedSessions);
      setSessions(updatedSessions);
      // Update projectSessions so the new session appears under the active project header
      if (sessionProjectId) {
        setProjectSessions(prev => {
          const filtered = prev.filter(s => s.id !== sessionId);
          return [newSession, ...filtered].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
        });
        // The project scope is persisted once the stream has ended: the server
        // has no row for this session until the generate request creates it.
      }
    } else {
      updateSessionMessages(sessionId, sessionInitialMessages, sessionStatus, true);
      setSessions(prev => {
        const updated = prev.map(session =>
          session.id === sessionId ? { ...session, sessionConfig } : session
        );
        sessionsRef.current = updated;
        saveToStorage(updated);
        return updated;
      });
    }

    setLoadingSessionId(sessionId);
    generatingSessionRef.current = sessionId;

    const controller = new AbortController();
    abortControllersRef.current.set(sessionId, controller);

    try {
      const requestStartedAt = performance.now();
      const stream = generatePlcCodeStream(prompt, turnTiaTarget.request, controller.signal, currentMessagesSnapshot, sessionId);
      let accumulatedText = '';
      let accumulatedReasoning = '';
      let accumulatedPlanThinkingReasoning = '';
      let accumulatedPlanAnalysisReasoning = '';
      let accumulatedPlanGenerationReasoning = '';
      let accumulatedPlanTransitionText = '';
      let accumulatedPlanProcessSummary: any = undefined;
      let accumulatedPlanProcessHistory: streamState.PlanProcessHistory = [];
      let accumulatedStageBuffers: NonNullable<Message['stageBuffers']> = {};
      let pipelinePayload = undefined;
      let lastUpdateTime = 0;
      let lastPersistTime = 0;
      let lastParseTime = 0;
      let firstTokenReceived = false;
      let sessionMessages = [...sessionInitialMessages];
      let isOffTopicResponse = false;
      let renderFlushTimer: number | undefined;
      let pendingFlushForcePersist = false;
      let pendingFlushForceParse = false;
      let pendingFlushIsStreaming = true;
      let lastParsedContent = undefined;
      let lastSessionStatePublishTime = 0;
      let latestGenerationMetrics: GenerationMetrics | undefined;
      let latestRepairAppendix: RepairAppendix | undefined;
      let latestProgressState: GenerationProgressState | undefined;
      let latestProcessStage: GenerationProcessStage | undefined;
      let latestStageCode = '';
      let latestReplacementParsed: ReturnType<typeof parseStreamedContent> | undefined;
      // V1 P0: TIA tool call state (local to this stream)
      let accumulatedToolCalls: streamState.ToolCallState[] = [];
      let accumulatedWorkflowWaiting: streamState.WorkflowWaitingState | null = null;

      const updateMessagesIfActive = () => {
        sessionMessages = mergeSubmittedPlanReviewResults(
          sessionMessages,
          messagesRef.current,
          sessionsRef.current.find(item => item.id === sessionId)?.messages,
        );
        if (currentSessionIdRef.current === sessionId) {
          setMessages(sessionMessages);
          messagesRef.current = sessionMessages;
        }
      };

      const applyGenerationMetrics = (incoming: Partial<GenerationMetrics>) => {
        latestGenerationMetrics = streamState.mergeGenerationMetrics(latestGenerationMetrics, incoming);

        sessionMessages = sessionMessages.map((message) =>
          {
            if (message.id !== assistantMessageId) return message;
            const generationMetrics = {
              ...(message.generationMetrics || {}),
              ...latestGenerationMetrics,
            };
            return {
              ...message,
              generationMetrics,
              tiaValidation: generationMetrics.tiaValidation || message.tiaValidation,
            };
          },
        );
      };

      const shouldPersistNow = (force = false) => {
        if (force) return true;
        return Date.now() - lastPersistTime >= streamState.STREAM_PERSIST_THROTTLE_MS;
      };

      const updateAssistantMessage = (updates: Partial<Message>) => {
        sessionMessages = sessionMessages.map(message =>
          message.id === assistantMessageId
            ? { ...message, ...updates }
            : message
        );
      };

      // A stage that finished with no stage_done after it — requirement
      // analysis waits for the next one — is sealed when the turn ends. See
      // sealFinishedStageConclusions.
      const sealFinishedStagesAtTurnEnd = () => {
        sessionMessages = sessionMessages.map((message) => {
          if (message.id !== assistantMessageId) return message;
          const sealed = sealFinishedStageConclusions(message, t);
          return sealed ? { ...message, stageBuffers: sealed } : message;
        });
      };

      const applyProgressState = (progressState: GenerationProgressState | undefined) => {
        if (!progressState) return;
        const stage = streamState.processStageFromProgressState(progressState);
        latestProcessStage = stage || latestProcessStage;
        latestProgressState = stage ? { ...progressState, stage } : progressState;
        // Review (runtime batch): read off the worker's own structured
        // `replanAttempt` counter, not the label's words — the Chinese
        // replanning label never matched the English-only regex this
        // replaced, so a Chinese turn's plan history never opened its own
        // 「重新规划中」 item. See `isReplanningProgressState`.
        if (streamState.isReplanningProgressState(progressState)) {
          accumulatedPlanProcessHistory = streamState.startPlanProcessHistoryItem(
            accumulatedPlanProcessHistory,
            'replanned',
            language === 'English' ? 'Re-planning in progress....' : '重新规划中....',
          );
        }
        updateAssistantMessage({
          progressState: latestProgressState,
          progress: typeof latestProgressState.progress === 'number' ? latestProgressState.progress : undefined,
          isStreaming: latestProgressState.phase !== 'completed' && latestProgressState.phase !== 'failed',
          planProcessHistory: accumulatedPlanProcessHistory.length ? accumulatedPlanProcessHistory : undefined,
        });
      };

      const applyRepairAppendix = (incoming: RepairAppendix) => {
        latestRepairAppendix = {
          ...(latestRepairAppendix || {}),
          ...incoming,
          attempts: incoming.attempts || latestRepairAppendix?.attempts || [],
        };
        updateAssistantMessage({
          repairAppendix: latestRepairAppendix,
          isStreaming: incoming.status === 'repairing',
        });
      };

      const updateSessionMessagesLocal = (
        isStreaming: boolean,
        forcePersist = false,
        sessionPatch: Partial<ChatSession> = {},
      ) => {
        const scopedSessionPatch: Partial<ChatSession> = sessionProjectId
          ? { projectId: sessionProjectId, ...sessionPatch }
          : sessionPatch;
        sessionMessages = mergeSubmittedPlanReviewResults(
          sessionMessages,
          messagesRef.current,
          sessionsRef.current.find(item => item.id === sessionId)?.messages,
        );
        const now = Date.now();
        const hasSessionPatch = Object.keys(scopedSessionPatch).length > 0;
        const shouldPublishSessionState = forcePersist
          || !isStreaming
          || hasSessionPatch
          || now - lastSessionStatePublishTime >= streamState.STREAM_SESSION_STATE_THROTTLE_MS;

        const applySessionUpdate = (items: ChatSession[]) =>
          items.map(session =>
            session.id === sessionId
              ? {
                  ...session,
                  messages: sessionMessages,
                  updatedAt: isStreaming && !forcePersist ? session.updatedAt : now,
                  isStreaming,
                  lastStreamingActivityAt: isStreaming ? now : session.lastStreamingActivityAt,
                  ...scopedSessionPatch,
                }
              : session,
          );

        sessionsRef.current = applySessionUpdate(sessionsRef.current);
        if (sessionProjectId) {
          const scopedProjectSessions = sessionsRef.current
            .filter(session => session.projectId === sessionProjectId)
            .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
          setProjectSessions(scopedProjectSessions);
        }

        if (shouldPersistNow(forcePersist)) {
          saveToStorage(sessionsRef.current);
          lastPersistTime = now;
        }

        if (!shouldPublishSessionState) {
          return;
        }

        lastSessionStatePublishTime = now;
        setSessions(sessionsRef.current);
      };

      const getParsedSnapshot = (force = false) => {
        if (
          pipelinePayload === undefined
            && accumulatedText.length === 0
            && accumulatedReasoning.length === 0
            && !latestReplacementParsed
            && !latestStageCode
        ) {
          return lastParsedContent;
        }

        const now = Date.now();
        if (!force && lastParsedContent !== undefined && now - lastParseTime < streamState.STREAM_PARSE_THROTTLE_MS) {
          return lastParsedContent;
        }

        const parsedFromText = parseStreamedContent(accumulatedText, accumulatedReasoning);
        lastParsedContent = latestReplacementParsed
          ? {
              ...(parsedFromText ?? {}),
              ...latestReplacementParsed,
              code: latestReplacementParsed.code || parsedFromText?.code || '',
              flowchart: latestReplacementParsed.flowchart || parsedFromText?.flowchart,
              summary: latestReplacementParsed.summary || parsedFromText?.summary,
              timestamp: Date.now(),
            }
          : parsedFromText;
        lastParseTime = now;
        return lastParsedContent;
      };

      const getEffectiveParsedSnapshot = (force = false) => {
        const parsed = getParsedSnapshot(force);
        const effectiveCode = latestStageCode || parsed?.code;
        return effectiveCode ? {
          code: effectiveCode,
          explanation: parsed?.explanation || '',
          thought: parsed?.thought || '',
          flowchart: parsed?.flowchart,
          summary: parsed?.summary,
          clarify: parsed?.clarify,
          verification: parsed?.verification,
          timestamp: Date.now(),
        } : parsed;
      };

      const applyStreamingContentSnapshot = (forceParse = false, isStreaming = true) => {
        const parsed = isOffTopicResponse ? undefined : getEffectiveParsedSnapshot(forceParse);
        updateAssistantMessage({
          content: accumulatedText,
          reasoning: accumulatedReasoning,
          planThinkingReasoning: accumulatedPlanThinkingReasoning || undefined,
          planReasoning: accumulatedReasoning || undefined,
          planAnalysisReasoning: accumulatedPlanAnalysisReasoning || undefined,
          planGenerationReasoning: accumulatedPlanGenerationReasoning || undefined,
          ...streamState.stageBufferPatch(accumulatedStageBuffers),
          planTransitionText: accumulatedPlanTransitionText || undefined,
          planProcessSummary: accumulatedPlanProcessSummary || undefined,
          planProcessHistory: accumulatedPlanProcessHistory.length ? accumulatedPlanProcessHistory : undefined,
          parsed,
          offTopic: isOffTopicResponse ? true : undefined,
          pipeline: isOffTopicResponse ? undefined : pipelinePayload,
          repairAppendix: latestRepairAppendix,
          progressState: latestProgressState,
          tiaValidation: latestGenerationMetrics?.tiaValidation,
          isStreaming,
        });
      };

      const clearScheduledStreamFlush = () => {
        if (renderFlushTimer) {
          window.clearTimeout(renderFlushTimer);
          renderFlushTimer = undefined;
        }
      };

      const flushStreamState = () => {
        clearScheduledStreamFlush();
        applyStreamingContentSnapshot(pendingFlushForceParse, pendingFlushIsStreaming);
        updateMessagesIfActive();
        updateSessionMessagesLocal(pendingFlushIsStreaming, pendingFlushForcePersist || !firstTokenReceived);
        firstTokenReceived = true;
        lastUpdateTime = Date.now();
        pendingFlushForcePersist = false;
        pendingFlushForceParse = false;
        pendingFlushIsStreaming = true;
      };

      const scheduleStreamFlush = (options: {
        force?: boolean;
        forcePersist?: boolean;
        forceParse?: boolean;
        isStreaming?: boolean;
      } = {}) => {
        pendingFlushForcePersist = pendingFlushForcePersist || Boolean(options.forcePersist);
        pendingFlushForceParse = pendingFlushForceParse || Boolean(options.forceParse);
        pendingFlushIsStreaming = options.isStreaming ?? pendingFlushIsStreaming;

        if (options.force || !firstTokenReceived) {
          flushStreamState();
          return;
        }

        const elapsed = Date.now() - lastUpdateTime;
        if (elapsed >= streamState.STREAM_RENDER_THROTTLE_MS) {
          flushStreamState();
          return;
        }

        if (!renderFlushTimer) {
          renderFlushTimer = window.setTimeout(flushStreamState, streamState.STREAM_RENDER_THROTTLE_MS - elapsed);
        }
      };

      try {
        for await (const chunk of stream) {
          // The gateway stored the turn under a conversation of its own: the
          // conversation goes on under that one (followServerSession).
          if (chunk.serverSessionId) {
            followServerSession(sessionId, chunk.serverSessionId);
            turnTiaTarget.renamed(chunk.serverSessionId);
            sessionId = chunk.serverSessionId;
            continue;
          }
          // What the server stored of the session's TIA target: with the turn
          // (its accepted chunk), or since (a `tia_target_resolved` chunk).
          if (turnTiaTarget.onChunk(chunk)) continue;
          if (controller.signal.aborted) break;
          if (chunk.status === 'switching' && accumulatedText.length < 100) accumulatedText = '';

          if (typeof chunk.progress === 'number') {
            applyProgressState(chunk.progressState || {
              phase: 'generating',
              label: 'Processing',
              progress: chunk.progress,
              updatedAt: Date.now(),
            });
            scheduleStreamFlush({ isStreaming: true });
            continue;
          }

          if (streamState.isStageStreamChunk(chunk)) {
            accumulatedStageBuffers = streamState.applyStageChunkToBuffers(accumulatedStageBuffers, chunk);
            // A stage that just ended decides its conclusion now and keeps it.
            // Left to be derived at render time, the row the engineer already
            // read changed whenever a later stage rewrote the field it was
            // derived from — see sealFinishedStageConclusions, which is told
            // which stage ended because requirement analysis waits for the next.
            if (chunk.type === 'stage_done') {
              const current = sessionMessages.find(message => message.id === assistantMessageId);
              const sealed = current
                ? sealFinishedStageConclusions({ ...current, stageBuffers: accumulatedStageBuffers }, t, chunk.stage)
                : undefined;
              if (sealed) accumulatedStageBuffers = sealed;
            }
            updateAssistantMessage({
              stageBuffers: accumulatedStageBuffers,
            });

            if (chunk.stage === 'code_generation') {
              const codeFromArtifact = chunk.type === 'stage_artifact'
                ? streamState.extractCodeFromStageArtifact(chunk.artifact)
                : '';
              const codeFromBuffer = streamState.extractCodeFromStageArtifact(
                accumulatedStageBuffers.codeGeneration?.text || '',
              );
              latestStageCode = codeFromArtifact || codeFromBuffer || latestStageCode;
            }

            if (chunk.type === 'stage_artifact' && chunk.stage === 'plan_review') {
              const currentPlanReview = sessionMessages.find(message => message.id === assistantMessageId)?.planReview;
              const validated = streamState.validatePlanReview(chunk.artifact, currentPlanReview);
              if (validated) {
                accumulatedPlanProcessHistory = streamState.applyPlanReviewToHistory(
                  accumulatedPlanProcessHistory,
                  validated as unknown as NonNullable<Message['planReview']>,
                );
                updateAssistantMessage({
                  planReview: validated as any,
                  planProcessHistory: accumulatedPlanProcessHistory,
                });
              }
            }

            // The preflight's own report, kept so the step that states the count
            // can also show the findings behind it. The runtime has always
            // emitted this artifact; nothing read it, so the panel fell back to
            // the post-repair report and went blank the moment a repair
            // succeeded — a heading reading "1 个错误、11 个警告" above nothing.
            if (chunk.type === 'stage_artifact' && chunk.stage === 'validation') {
              // ...and written once. The repair loop re-runs validation each
              // round and each run emits one of these, so an unconditional
              // write left this field holding whichever validation ran last —
              // under a heading the runtime had already sealed. 2026-09-15：
              // 预检那一行在「未通过：3 个错误、17 个警告」下面写着「8 类根因
              // （共 20 处）」，编译段开始后同一行变成「6 类根因（共 17 处）」。
              updateAssistantMessage(streamState.preflightReportPatch(
                sessionMessages.find(message => message.id === assistantMessageId)?.preflightReport,
                chunk.artifact,
              ));
            }

            if (chunk.type === 'stage_artifact' && chunk.stage === 'requirement_confirmation') {
              const artifact = chunk.artifact as any;
              const items = Array.isArray(artifact?.items) ? artifact.items : undefined;
              if (items?.length) {
                const normalizedChecklist = streamState.dedupeClarificationItems(items as any) as any;
                const clarificationGates = getWorkflowStageGates({
                  id: assistantMessageId,
                  role: 'assistant',
                  content: accumulatedText || '',
                  timestamp: Date.now(),
                  clarificationChecklist: normalizedChecklist,
                } as Message);
                updateAssistantMessage({
                  clarificationChecklist: normalizedChecklist,
                  pendingChipIds: clarificationGates.pendingRequiredHumanConfirmationIds,
                });
              }
            }

            scheduleStreamFlush({
              force: chunk.type !== 'stage_delta',
              forcePersist: chunk.type !== 'stage_delta',
              isStreaming: true,
            });
          }

          // V1 P0: TIA Tool Call events
          if (streamState.isToolCallChunk(chunk)) {
            const tc = chunk as any;
            if (tc.type === 'tool_call_start') {
              accumulatedToolCalls = streamState.applyToolCallStart(accumulatedToolCalls, tc);
            } else if (tc.type === 'tool_progress') {
              accumulatedToolCalls = streamState.applyToolProgress(accumulatedToolCalls, tc);
            } else if (tc.type === 'tool_result') {
              accumulatedToolCalls = streamState.applyToolResult(accumulatedToolCalls, tc);
            } else if (tc.type === 'tool_error') {
              accumulatedToolCalls = streamState.applyToolError(accumulatedToolCalls, tc);
            }
            updateAssistantMessage({ toolCalls: accumulatedToolCalls } as any);
            scheduleStreamFlush({ force: true, forcePersist: true, isStreaming: true });
          }

          // V1 P0: Workflow waiting / resumed events
          if (streamState.isWorkflowWaitingChunk(chunk)) {
            accumulatedWorkflowWaiting = {
              workflowRunId: (chunk as any).workflowRunId,
              stage: (chunk as any).stage,
              stageStatus: (chunk as any).stageStatus,
              blocker: (chunk as any).blocker,
              message: (chunk as any).message,
              timestamp: (chunk as any).timestamp,
            };
            updateAssistantMessage({ tiaWorkflowWaiting: accumulatedWorkflowWaiting } as any);
            scheduleStreamFlush({ force: true, forcePersist: true, isStreaming: true });
          }

          if (streamState.isWorkflowResumedChunk(chunk)) {
            accumulatedWorkflowWaiting = null;
            updateAssistantMessage({ tiaWorkflowWaiting: null } as any);
            scheduleStreamFlush({ force: true, forcePersist: true, isStreaming: true });
          }

          if (chunk.metadata?.recoveryState === 'reconnecting') {
            applyProgressState({ ...(latestProgressState || { phase: 'queued', stage: 'routing', updatedAt: Date.now() }),
              label: language === 'Chinese' ? '连接中断，正在恢复原任务…' : 'Connection interrupted; reconnecting to the existing job…', updatedAt: Date.now() });
          }
          if (chunk.metadata) {
            const activeJobId = typeof chunk.metadata.jobId === 'string'
              ? chunk.metadata.jobId
              : (typeof chunk.metadata.requestId === 'string' ? chunk.metadata.requestId : undefined);
            if (activeJobId) {
              activeJobIdsRef.current.set(sessionId, activeJobId);
            }
            if (!latestProgressState || latestProgressState.phase === 'queued') {
              if (typeof chunk.metadata.timings?.queueEnqueuedAt === 'number') {
                applyProgressState(streamState.buildClientProgressState(
                  language,
                  'queued',
                  '已入队，等待工作节点处理...',
                  'Queued, waiting for a worker...',
                  2,
                ));
              } else if (typeof chunk.metadata.timings?.gatewayAcceptedAt === 'number') {
                applyProgressState(streamState.buildClientProgressState(
                  language,
                  'queued',
                  '请求已接收，正在加入队列...',
                  'Request accepted, queueing...',
                  1,
                ));
              }
            }
            // Phase 2: Capture plan review metadata
            if (chunk.metadata.planReview) {
              const currentPlanReview = sessionMessages.find(message => message.id === assistantMessageId)?.planReview;
              const validated = streamState.validatePlanReview(chunk.metadata.planReview, currentPlanReview);
              if (validated) {
                accumulatedPlanProcessHistory = streamState.applyPlanReviewToHistory(
                  accumulatedPlanProcessHistory,
                  validated as unknown as NonNullable<Message['planReview']>,
                );
                updateAssistantMessage({
                  planReview: validated as any,
                  planProcessHistory: accumulatedPlanProcessHistory,
                });
                scheduleStreamFlush({ isStreaming: true, force: true, forcePersist: true });
              }
            }
            // Phase 3: Capture clarification checklist metadata
            if (chunk.metadata?.clarificationChecklist && chunk.metadata.clarificationChecklist.length > 0) {
              const normalizedChecklist = streamState.dedupeClarificationItems(chunk.metadata.clarificationChecklist as any) as any;
              const clarificationGates = getWorkflowStageGates({
                id: assistantMessageId,
                role: 'assistant',
                content: accumulatedText || '',
                timestamp: Date.now(),
                clarificationChecklist: normalizedChecklist,
              } as Message);
              updateAssistantMessage({
                clarificationChecklist: normalizedChecklist,
                // Keep the UI gate blocked while required manual choices are still unanswered.
                // The previous code reset this to [], so the pipeline jumped into planning
                // even though the confirmation card still showed 0/N required answers.
                pendingChipIds: clarificationGates.pendingRequiredHumanConfirmationIds,
              });
              scheduleStreamFlush({ isStreaming: true, forcePersist: true });
            }
            // Capture clarify session state for follow-up requests
            if (chunk.metadata?.clarifyState) {
              const cs = chunk.metadata.clarifyState as any;
              updateAssistantMessage({
                clarifyState: cs,
              });
              scheduleStreamFlush({ isStreaming: true });
            }
            applyGenerationMetrics(streamState.generationMetricsFromStreamMetadata(chunk.metadata));
            updateMessagesIfActive();
            updateSessionMessagesLocal(true, false, {
              lastProvider: typeof chunk.metadata.provider === 'string' ? chunk.metadata.provider : undefined,
              lastModel: typeof chunk.metadata.model === 'string' ? chunk.metadata.model : undefined,
            });
            if (!hasDisplayPayloadAfterMetadata(chunk)) {
              continue;
            }
          }

          // Handle streaming clarify chip events (root-level chunk fields)
          if (chunk.chipPending) {
            const currentPending = sessionMessages.find(m => m.id === assistantMessageId)?.pendingChipIds || [];
            updateAssistantMessage({
              pendingChipIds: currentPending.includes(chunk.chipPending.id)
                ? currentPending
                : [...currentPending, chunk.chipPending.id],
            });
            scheduleStreamFlush({ isStreaming: true, forcePersist: true });
          }

          if (chunk.clarifyChip) {
            const currentChecklist = sessionMessages.find(m => m.id === assistantMessageId)?.clarificationChecklist || [];
            const updatedChecklist = streamState.dedupeClarificationItems([
              ...(currentChecklist as any),
              chunk.clarifyChip as any,
            ]);
            const currentPending = sessionMessages.find(m => m.id === assistantMessageId)?.pendingChipIds || [];
            const updatedPending = currentPending.filter(id => id !== chunk.clarifyChip!.id);
            updateAssistantMessage({
              clarificationChecklist: updatedChecklist as any,
              pendingChipIds: updatedPending,
            });
            scheduleStreamFlush({ isStreaming: true, forcePersist: true });
          }

          if (chunk.quota) {
            window.dispatchEvent(new CustomEvent('tenant_quota_updated', { detail: chunk.quota }));
            if (!chunk.pipeline && !chunk.repairAppendix && !chunk.repair && !chunk.text && !chunk.reasoning && !chunk.thinking && chunk.offTopic !== true && !chunk.progressState) {
              continue;
            }
          }

          const chunkStage = streamState.normalizeProcessStage(chunk.stage) || streamState.processStageFromProgressState(chunk.progressState) || latestProcessStage;
          if (chunkStage) {
            latestProcessStage = chunkStage;
          }

          if (chunk.progressState) {
            applyProgressState(chunk.progressState);
            scheduleStreamFlush({
              isStreaming: chunk.progressState.phase !== 'completed' && chunk.progressState.phase !== 'failed',
            });
            if (!chunk.pipeline && !chunk.repairAppendix && !chunk.repair && !chunk.text && !chunk.reasoning && !chunk.thinking && chunk.offTopic !== true) {
              continue;
            }
          }

          if (chunk.pipeline) {
            pipelinePayload = chunk.pipeline;
            updateAssistantMessage({ offTopic: false, pipeline: pipelinePayload, isStreaming: true });
            scheduleStreamFlush({ isStreaming: true });
            // Same rule the quota and progress branches above already follow: a
            // chunk is not finished with just because it carried a pipeline.
            if (!hasDisplayPayloadAfterPipeline(chunk)) {
              continue;
            }
          }

          if (chunk.repairAppendix) {
            applyRepairAppendix(chunk.repairAppendix);
            scheduleStreamFlush({
              force: chunk.repairAppendix.status !== 'repairing',
              forcePersist: true,
              isStreaming: chunk.repairAppendix.status === 'repairing',
            });
            continue;
          }

          if (chunk.repair) {
            pipelinePayload = chunk.repair.pipeline || pipelinePayload;
            const legacyRepairAppendix: RepairAppendix = {
              status: chunk.repair.content ? 'completed' : 'repairing',
              attempts: latestRepairAppendix?.attempts || [],
              finalCode: chunk.repair.content,
              summary: chunk.repair.reasoning,
              updatedAt: Date.now(),
            };
            applyRepairAppendix(legacyRepairAppendix);
            updateAssistantMessage({ offTopic: false, pipeline: pipelinePayload });
            scheduleStreamFlush({ force: legacyRepairAppendix.status !== 'repairing' });
            continue;
          }

          if (chunk.offTopic === true) {
            pipelinePayload = undefined;
            isOffTopicResponse = true;
          }
          if (chunk.thinking) {
            accumulatedPlanThinkingReasoning += chunk.thinking;
            accumulatedPlanProcessHistory = streamState.appendPlanProcessReasoning(
              accumulatedPlanProcessHistory,
              chunk.thinking,
              chunkStage || 'routing',
            );
          }
          if (chunk.reasoning) {
            accumulatedReasoning += chunk.reasoning;
            if (chunkStage !== 'engineering_reasoning') {
              accumulatedPlanProcessHistory = streamState.appendPlanProcessReasoning(
                accumulatedPlanProcessHistory,
                chunk.reasoning,
                chunkStage || 'requirement_analysis',
              );
            }
          }
          if (chunk.planAnalysisReasoning) {
            accumulatedPlanAnalysisReasoning += chunk.planAnalysisReasoning;
          }
          if (chunk.planGenerationReasoning) {
            accumulatedPlanGenerationReasoning += chunk.planGenerationReasoning;
          }
          if (chunk.planTransitionText) {
            accumulatedPlanTransitionText = chunk.planTransitionText as string;
          }
          if (chunk.planProcessSummary) {
            const planProcessSummary = chunk.planProcessSummary as PlanProcessSummary;
            accumulatedPlanProcessSummary = planProcessSummary;
            accumulatedPlanProcessHistory = streamState.applyPlanProcessSummary(
              accumulatedPlanProcessHistory,
              planProcessSummary,
            );
          }
          if (chunk.text) {
            const hasStructuredStageContent = Object.keys(accumulatedStageBuffers).length > 0;
            if (chunk.replaceContent) {
              accumulatedText = chunk.text;
            }
            if (chunk.replaceContent && hasStructuredStageContent) {
              const replacementParsed = parseStreamedContent(chunk.text, accumulatedReasoning);
              if (replacementParsed) {
                const replacementBelongsToRepair = Boolean(accumulatedStageBuffers.repair?.started);
                const preservedBaseCode = latestReplacementParsed?.code
                  || lastParsedContent?.code
                  || latestStageCode
                  || replacementParsed.code
                  || '';
                latestReplacementParsed = {
                  ...(latestReplacementParsed ?? {}),
                  ...replacementParsed,
                  code: replacementBelongsToRepair ? preservedBaseCode : replacementParsed.code || preservedBaseCode,
                  flowchart: replacementParsed.flowchart || latestReplacementParsed?.flowchart,
                  summary: replacementParsed.summary || latestReplacementParsed?.summary,
                  timestamp: Date.now(),
                };
                if (replacementParsed.code && !replacementBelongsToRepair) {
                  latestStageCode = replacementParsed.code;
                }
              }
            } else if (!chunk.replaceContent) {
              accumulatedText += chunk.text;
            }
            if (latestGenerationMetrics?.clientFirstTokenMs === undefined) {
              applyGenerationMetrics({
                clientFirstTokenMs: performance.now() - requestStartedAt,
              });
            }
          }

          scheduleStreamFlush({ forcePersist: !firstTokenReceived, isStreaming: true });
        }

        clearScheduledStreamFlush();
        sessionMessages = sessionMessages.map(message =>
          message.id === assistantMessageId
            ? {
                ...message,
                content: accumulatedText,
                reasoning: accumulatedReasoning,
                planThinkingReasoning: accumulatedPlanThinkingReasoning || message.planThinkingReasoning,
                planReasoning: accumulatedReasoning || message.planReasoning,
                planAnalysisReasoning: accumulatedPlanAnalysisReasoning || message.planAnalysisReasoning,
                planGenerationReasoning: accumulatedPlanGenerationReasoning || message.planGenerationReasoning,
                ...streamState.stageBufferPatch(accumulatedStageBuffers),
                planTransitionText: accumulatedPlanTransitionText || message.planTransitionText,
                planProcessSummary: accumulatedPlanProcessSummary || message.planProcessSummary,
                planProcessHistory: message.planProcessHistory?.length
                  ? message.planProcessHistory
                  : accumulatedPlanProcessHistory.length
                    ? accumulatedPlanProcessHistory
                    : undefined,
                // The server's verdict has the last word on whether there is a
                // program; see `finalParsedSnapshot`.
                parsed: isOffTopicResponse
                  ? undefined
                  : streamState.finalParsedSnapshot(getEffectiveParsedSnapshot(true), accumulatedStageBuffers),
                offTopic: isOffTopicResponse ? true : message.offTopic,
                pipeline: isOffTopicResponse ? undefined : pipelinePayload,
                repairAppendix: latestRepairAppendix,
                progressState: controller.signal.aborted
                  ? {
                      phase: 'failed',
                      stage: 'failed',
                      label: language === 'English' ? 'Generation stopped' : '生成已停止',
                      progress: message.progress,
                      updatedAt: Date.now(),
                    }
                  : latestProgressState?.phase === 'failed'
                    ? latestProgressState
                    : {
                        phase: 'completed',
                        stage: 'completed',
                        label: streamState.hasTruncatedModelAttempt(latestGenerationMetrics)
                          ? (language === 'English'
                            ? 'Output limit reached; response may be incomplete'
                            : '输出长度到达上限，回复可能不完整')
                          : latestRepairAppendix
                            ? (language === 'English' ? 'Automatic repair completed' : '自动修复完成')
                            : (language === 'English' ? 'Generation completed' : '生成完成'),
                        progress: 100,
                        updatedAt: Date.now(),
                      },
                isStreaming: false,
              }
            : message
        );
        sealFinishedStagesAtTurnEnd();
        applyGenerationMetrics({
          clientTotalMs: performance.now() - requestStartedAt,
        });
        updateMessagesIfActive();
        updateSessionMessagesLocal(false, true);
        // P2-3: Auto-generate a better session title after first complete response
        autoGenerateSessionTitle(sessionId);
        // When a session completes with project output, reset the viewed flag
        // so the green dot appears in the sidebar to notify the user.
        const completedSession = sessionsRef.current.find(s => s.id === sessionId);
        if (completedSession && sessionHasCompletedOutput(completedSession.messages)) {
          setViewedSessionIds(prev => {
            if (!prev.has(sessionId)) return prev;
            const next = new Set(prev);
            next.delete(sessionId);
            try { localStorage.setItem('realplc_viewed_sessions', JSON.stringify([...next])); } catch { /* ignore */ }
            return next;
          });
        }
        if (authUser && sessionProjectId) {
          void persistProjectScope(sessionId, sessionProjectId);
        }
      } catch (error: any) {
        if (error.name === 'AbortError') return;
        clearScheduledStreamFlush();
        const statusPending = error.name === 'GenerationRecoveryPendingError';
        const errorMsg = statusPending ? '' : `\n\n*[Error]* ${error.message || 'Generation failed'}`;
        sessionMessages = sessionMessages.map(message =>
          message.id === assistantMessageId ? {
            ...message,
            content: `${accumulatedText || message.content}${errorMsg}`,
            recoveryJobId: statusPending ? error.jobId : undefined,
            reasoning: accumulatedReasoning || message.reasoning,
            planThinkingReasoning: accumulatedPlanThinkingReasoning || message.planThinkingReasoning,
            planReasoning: accumulatedReasoning || message.planReasoning,
            planAnalysisReasoning: accumulatedPlanAnalysisReasoning || message.planAnalysisReasoning,
            planGenerationReasoning: accumulatedPlanGenerationReasoning || message.planGenerationReasoning,
            ...streamState.stageBufferPatch(accumulatedStageBuffers),
            planTransitionText: accumulatedPlanTransitionText || message.planTransitionText,
            planProcessSummary: accumulatedPlanProcessSummary || message.planProcessSummary,
            planProcessHistory: message.planProcessHistory?.length
              ? message.planProcessHistory
              : accumulatedPlanProcessHistory.length
                ? accumulatedPlanProcessHistory
                : undefined,
            parsed: isOffTopicResponse
              ? undefined
              : streamState.finalParsedSnapshot(getEffectiveParsedSnapshot(true), accumulatedStageBuffers),
            pipeline: isOffTopicResponse ? undefined : pipelinePayload,
            repairAppendix: latestRepairAppendix,
            progressState: {
              phase: statusPending ? 'queued' : 'failed',
              stage: statusPending ? 'routing' : 'failed',
              label: statusPending ? (language === 'Chinese' ? '任务状态暂未确认，可继续查询' : 'Job status is unconfirmed; continue checking') : error.message || 'Generation failed',
              progress: message.progress,
              updatedAt: Date.now(),
            },
            isStreaming: false,
          } : message
        );
        sealFinishedStagesAtTurnEnd();
        applyGenerationMetrics({
          clientTotalMs: performance.now() - requestStartedAt,
        });
        updateMessagesIfActive();
        updateSessionMessagesLocal(false, true);
        if (authUser && sessionProjectId) {
          void persistProjectScope(sessionId, sessionProjectId);
        }
      } finally {
        clearScheduledStreamFlush();
        abortControllersRef.current.delete(sessionId as string);
        activeJobIdsRef.current.delete(sessionId as string);
        if (generatingSessionRef.current === sessionId) generatingSessionRef.current = null;
        if (currentSessionIdRef.current === sessionId) {
          setLoadingSessionId(null);
        }

        // A stream that ends without the turn reaching an ending did not
        // deliver the turn — it dropped, and the server carried on without us.
        //
        // 2026-09-16 13:49–13:57 线上：客户端最后一次收到进度是 13:49:04；服务端
        // 一路跑到 13:57:15，重试了一次生成（304 秒），最后发出
        // CODE_GENERATION_INCOMPLETE、关掉阶段、推 100% 进度——全发进了一个已经
        // 断掉的连接。屏幕上留下的是停在 planning 的半截状态：「用时 1分26秒」
        // （真实 9分36秒），codeGeneration 永远停在 streaming，而代码生成失败过
        // 两次这件事一个字都没说。
        //
        // 这个谓词本来就答得出——不再 streaming 却停在非终态阶段——只是从来没有
        // 人在流结束的这一刻问它。问一次，服务端那份完整记录就回来了。
        const settled = sessionMessages.find(message => message.id === assistantMessageId);
        if (settled && !settled.recoveryJobId && shouldHydrateRestoredSession({ messages: [settled] })) {
          void hydrateSessionMessages(sessionId as string);
        }
      }
    } catch (error) {
      console.error('Outer stream error:', error);
    }
  }, [activeProjectId, authUser, currentSession, followServerSession, language, saveToStorage, sessionSnapshotWriter, sidebarView, storageScope, updateSessionMessages]);

  // Resume a timed-out plan — user clicked "继续执行" button or typed "继续"
  const handleResumePlan = useCallback((
    planReview: NonNullable<Message['planReview']>,
    _requestId: string,
  ) => {
    if (!planReview?.subtasks?.length) return;
    // Resuming an approved plan continues the same piece of work, so it has to
    // run against the same target as the plan it is resuming.
    //
    // This used to pass `{ preApprovedPlan } as any` — no brand, no language, no
    // model — and `as any` kept the compiler quiet about it. Everything missing
    // was then filled with defaults, so a CODESYS/ST session resumed as
    // Siemens/SCL: the CODESYS compile gate was skipped for the entire round
    // ("本轮目标不是 CODESYS"), the artifact landed as .scl, and the finished
    // turn relabelled itself AutoFree.
    const snapshot = currentSession?.sessionConfig;
    if (!snapshot?.brand || !snapshot?.language) {
      console.error('[resume] refusing to resume without the session target configuration');
      return;
    }
    handleGenerate(
      planReview.requirementSummary || RESUME_PLAN_FALLBACK_PROMPT,
      {
        brand: snapshot.brand,
        language: snapshot.language,
        projectId: snapshot.projectId,
        preferredProvider: snapshot.preferredProvider,
        preferredModel: snapshot.preferredModel,
        model: snapshot.preferredModel,
        routingMode: snapshot.routingMode,
        targetProfile: snapshot.targetProfile,
        softwareVersion: snapshot.softwareVersion,
        hardwareSeries: snapshot.hardwareSeries,
        // The user's setting; the snapshot's `plcModel` is the server's (P2 E9).
        settingsPlcModel: sessionSettingsPlcModel(snapshot),
        firmwareVersion: snapshot.firmwareVersion,
        preApprovedPlan: planReview as unknown as Record<string, unknown>,
      } as TurnGenerationConfig,
      { resumesPlan: true },
    );
  }, [handleGenerate, currentSession]);

  if (!authReady) {
    return <div className="flex h-screen items-center justify-center bg-[var(--color-bg-page)] text-[var(--color-text-secondary)]">Loading...</div>;
  }

  if (view === 'admin' && hasAdminConsoleAccess(authUser)) {
    return (
      <>
      <ToastProvider>
        <React.Suspense fallback={null}><AdminDashboard onBack={() => setView('workspace')} onLogout={handleLogoutSimple} isPlatformSuperAdmin={authUser?.role === 'super_admin'} /></React.Suspense>
      </ToastProvider>
      <SettingsModal
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          authUser={authUser}
          onOpenAccount={handleOpenAccount}
          onLogout={handleLogoutSimple}
        />
        <AccountCenter
          isOpen={isAccountOpen}
          initialSection={accountInitialSection}
          authUser={authUser}
          onClose={() => setIsAccountOpen(false)}
          onUserUpdated={setAuthUser}
          onLogout={handleLogoutSimple}
        />
      </>
    );
  }

  if (view === 'landing') {
    return <LandingPageV3 onStart={handleOpenSignin} onTryDemo={(_prompt) => {
      // 直接进入工作区，携带 prompt
      setView('workspace');
      // 可以在这里触发生成，或者存储 prompt 待用
    }} />;
  }

  if (view === 'login') {
    return (
      <div className="relative min-h-screen">
        <LoginGatewayPageV3
          authError={authError}
          isSubmitting={authSubmitting}
          onBack={() => setView('landing')}
          onLogin={handleLogin}
          onTryDemo={(_prompt) => {
            // 体验 Demo：直接进入工作区并可以使用 prompt
            setView('workspace');
          }}
          version={publicAppVersion}
        />
      </div>
    );
  }

  return (
    <DiagnosticsVisibilityContext.Provider value={hasAdminConsoleAccess(authUser)}>
    <ToastProvider>
    <AppShortcuts
      onFocusSearch={focusSessionSearch}
      onNewChat={handleNewChat}
      onStopGeneration={handleStopGeneration}
      isGenerating={Boolean(loadingSessionId)}
    />
    <div className="h-screen w-full bg-[var(--color-bg-page)] flex overflow-hidden text-[var(--color-text-primary)] font-sans selection:bg-cyan-500/30">
      <ErrorBoundary>
        <SidebarSecure
          isOpen={isSidebarOpen}
          onToggle={handleToggleSidebar}
          onHome={handleGoHome}
          onNewChat={handleNewChat}
          onOpenSettings={() => setIsSettingsOpen(true)}
          sessions={sessions}
          currentSessionId={currentSessionId}
          onSelectSession={handleSelectSession}
          onDeleteSession={handleDeleteSession}
          onRenameSession={handleRenameSession}
          onStarSession={handleStarSession}
          authUser={authUser}
          onOpenAdmin={hasAdminConsoleAccess(authUser) ? () => setView('admin') : undefined}
          onOpenAccount={() => handleOpenAccount('profile')}
          onLogout={handleLogoutSimple}
          projects={projects}
          sidebarView={sidebarView}
          activeProjectId={activeProjectId}
          activeProjectName={activeProjectName}
          projectSessions={projectSessions}
          starredSessions={starredSessions}
          onBackFromProject={handleBackFromProject}
          onNewProjectChat={handleNewProjectChat}
          onOpenCreateProject={handleOpenCreateProject}
          onEnterProject={handleEnterProject}
          onDeleteProject={handleDeleteProject}
          onRenameProject={handleRenameProject}
          viewedSessionIds={viewedSessionIds}
        />

        <main className="flex-1 min-w-0 h-full flex flex-col relative bg-[var(--color-bg-card)] overflow-hidden">
          <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_50%_40%,rgba(14,165,233,0.03)_0%,transparent_70%)]" />
          {!isSidebarOpen && (
            <>
              <button onClick={handleToggleSidebar} aria-label={t('Open sidebar')} className="absolute top-3 left-4 z-20 p-2 text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-surface)] rounded-lg transition-colors md:hidden">
                <Menu size={20} />
              </button>
              <div className="md:hidden">
                <FloatingUserButton authUser={authUser} onClick={() => handleOpenAccount('profile')} />
              </div>
            </>
          )}
          <div className="flex flex-1 overflow-hidden">
            {/* Main chat area */}
            <div className="flex flex-1 flex-col min-w-0">
              <ChatArea
                messages={activePathMessages}
                allMessages={messages}
                activeBranches={activeBranchesBySession[currentSessionId || DRAFT_BRANCH_KEY] || EMPTY_BRANCHES}
                onSelectBranch={handleSelectBranch}
                isLoading={loadingSessionId === currentSessionId && !!loadingSessionId}
                historyState={resolveConversationHistoryState(
                  currentSessionId,
                  historyLoadingSessionId,
                  historyLoadErrorSessionId,
                )}
                onRetryHistory={currentSessionId ? () => { void handleSelectSession(currentSessionId); } : undefined}
                onNewConversation={handleNewChat}
                onSend={handleGenerate}
                onStop={handleStopGeneration}
                activeSession={currentSession}
                activeProjectId={sidebarView === 'project' ? activeProjectId : null}
                isSuperAdmin={authUser?.role === 'super_admin'}
                onPlanReviewSubmitted={markPlanReviewSubmitted}
                onResumePlan={handleResumePlan}
                onOpenRequirementWorkbook={handleOpenWorkspace}
                onTestHintsResolved={handleTestHintsResolved}
              />
            </div>

            {/*
              One right-hand slot, one occupant. A file preview the user opened
              is the more specific request, so it wins; the workspace returns as
              soon as the preview closes.
            */}
            <React.Suspense fallback={null}>
              {filePreviewOpen ? (
                <FilePreviewPanel />
              ) : (
                <EngineeringWorkspace
                  viewModel={workspaceViewModel}
                  expandLevel={workspaceExpandLevel}
                  onClose={handleCloseEngineeringWorkspace}
                  onOpen={() => setCurrentWorkspaceExpandLevel(hasActivePipeline ? 'dashboard' : 'full')}
                  onExpand={() => setCurrentWorkspaceExpandLevel('full')}
                  onTabChange={setCurrentWorkspaceTab}
                  messages={messages}
                  versionChains={artifactVersionChains}
                />
              )}
            </React.Suspense>
          </div>
        </main>

        <SettingsModal
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          authUser={authUser}
          onOpenAccount={handleOpenAccount}
          onLogout={handleLogout}
        />
        <AccountCenter
          isOpen={isAccountOpen}
          initialSection={accountInitialSection}
          authUser={authUser}
          onClose={() => setIsAccountOpen(false)}
          onUserUpdated={setAuthUser}
          onLogout={handleLogout}
        />
        <CreateProjectModal
          isOpen={isCreateProjectModalOpen}
          onClose={() => setIsCreateProjectModalOpen(false)}
          onCreate={handleCreateProject}
        />
      </ErrorBoundary>
    </div>
    </ToastProvider>
    </DiagnosticsVisibilityContext.Provider>
  );
}

export default AppSecure;
